/**
 * src/data/funds.js — danh sách quỹ để công cụ khớp hồ sơ đối chiếu.
 *
 * ─────────────────────────────────────────────────────────────────────────
 * QUY TẮC BẤT DI BẤT DỊCH KHI THÊM QUỸ
 *
 * 1. Chỉ điền trường mà quỹ NÓI RÕ trên trang chính thức của họ. Không suy ra
 *    từ danh mục đầu tư, không lấy từ trang tổng hợp bên thứ ba, không đoán.
 * 2. Không biết thì ĐỂ TRỐNG. Engine coi chiều để trống là "quỹ không giới
 *    hạn" và vẫn khớp được. Điền số đoán bừa tạo ra kết luận sai mà người dùng
 *    không có cách nào biết — và họ mất hàng tuần gửi hồ sơ nhầm chỗ.
 * 3. Mọi quỹ thật phải có `provenance.sources` và `lastVerifiedAt`.
 *    `npm run check-funds` chạy trong mỗi lần build và dừng build nếu thiếu.
 * 4. Chưa xác nhận được kênh nộp hồ sơ chính thức thì để
 *    `publish.status: 'DRAFT'` — không hiển thị cho tới khi có.
 *
 * Ở giai đoạn 2, danh sách này chuyển sang Firestore collection `funds` và mỗi
 * quỹ có trang riêng tại /quy-dau-tu/{slug}.
 * ─────────────────────────────────────────────────────────────────────────
 */

/**
 * Quỹ công bố quy mô séc bằng USD. Lưu nguyên đơn vị gốc kèm tỷ giá và ngày áp
 * tỷ giá để sau này còn kiểm chứng lại được — đây chính là lý do shape Money
 * bắt buộc có `fx` và `fxDate` với mọi loại tiền không phải VND.
 *
 * Tỷ giá 26.170 VND/USD: giá bán ra Vietcombank ngày 09/09/2026.
 */
import REMOTE_FUNDS from './fundsRemote.js';
import { gopQuy } from '../lib/fundModel.js';

const TY_GIA_USD = 26170;
const NGAY_TY_GIA = '2026-09-09';
const usd = (n) => ({
  amount: Math.round(n * 100), ccy: 'USD', fx: TY_GIA_USD, fxDate: NGAY_TY_GIA,
});

export const FUNDS_GOC = [
  {
    fundId: 'do-ventures',
    name: 'Do Ventures',
    shortName: 'Do Ventures',
    slug: 'do-ventures',
    type: 'VC',
    vietnamPresence: 'OFFICE',
    website: 'https://doventures.vc',
    publish: { status: 'PUBLISHED' },

    thesis: {
      capitalTypes: ['EQUITY'],
      // Bốn nhóm ngành quỹ nêu trong mục "Investment Strategy" trên trang giới
      // thiệu: AI, Climate, Consumer Technology, Manufacturing Technology.
      industries: [
        'AI', 'CLIMATE', 'EDTECH', 'HEALTHTECH', 'FINTECH',
        'RETAILTECH', 'MANUFACTURING', 'ROBOTICS', 'LOGISTICS',
      ],
      // Quỹ tự mô tả là "early-stage" nhưng KHÔNG liệt kê giai đoạn cụ thể.
      // Để trống thay vì tự quy đổi "early-stage" thành pre-seed/seed/Series A.
      stages: [],
      geos: ['VN', 'SEA'],
      // Không công bố quy mô séc → không có trường ticket.
      businessModels: [],
      customerTypes: [],
      instruments: [],
    },

    process: {
      submitUrl: 'https://doventures.vc/en/contact',
      notes: 'Trang liên hệ có nút "Share your pitch deck" kèm biểu mẫu tải deck lên.',
    },

    content: {
      summary:
        'Quỹ đầu tư giai đoạn sớm, đầu tư vào công ty công nghệ tại Việt Nam và '
        + 'Đông Nam Á. Chiến lược xoay quanh hai nhóm ngành chính là công nghệ '
        + 'tiêu dùng và công nghệ sản xuất, trên nền tảng AI và khí hậu.',
      lookingFor: [
        'Công nghệ tiêu dùng: giáo dục, y tế, dịch vụ tài chính, thương hiệu tiêu dùng thế hệ mới',
        'Công nghệ sản xuất: tự động hoá công nghiệp, chuỗi cung ứng bền vững, giải pháp nhà máy',
        'Công ty gốc AI và công ty ứng dụng AI trong hai nhóm ngành trên',
      ],
      tips: [
        'Quỹ không công bố quy mô séc lẫn giai đoạn cụ thể trên trang chính thức. '
        + 'Hỏi thẳng trong buổi đầu thay vì đoán.',
      ],
    },


    // Danh mục lấy từ trang danh mục chính thức của quỹ. Con số là TỔNG VÒNG
    // được công bố, không phải séc riêng của quỹ này — xem chú thích trong
    // chuanHoaDanhMuc() ở src/lib/fundModel.js.
    portfolio: {
      source: 'https://doventures.vc/portfolio',
      verifiedAt: '2026-09-25',
      items: [
        {
          name: 'Coolmate',
          url: 'https://www.coolmate.me',
          whatTheyDo:
            'Thương hiệu thời trang nam bán thẳng tới người mua, không qua đại lý.',
          industries: ['ECOMMERCE', 'RETAILTECH'],
          stage: 'LATER',
          geo: 'VN',
          roundDate: '2024-10',
          roundSize: usd(6000000),
          roundSource:
            'https://vir.com.vn/coolmate-wraps-up-6-million-series-b-funding-led-by-vertex-ventures-116900.html',
          leadInvestor: false,
        },
        {
          name: 'Bizzi',
          url: 'https://bizzi.vn',
          whatTheyDo:
            'Tự động đọc và đối chiếu hoá đơn đầu vào cho phòng kế toán doanh nghiệp.',
          industries: ['SAAS_B2B', 'AI'],
          stage: 'SEED_PLUS',
          geo: 'VN',
          roundDate: '2021-10',
          roundSize: usd(3000000),
          roundSource:
            'https://vir.com.vn/bizzi-closes-3-million-pre-series-a-round-88283.html',
          leadInvestor: false,
        },
        {
          name: 'VUIHOC',
          whatTheyDo:
            'Học trực tuyến bám chương trình phổ thông cho học sinh tiểu học và trung học.',
          industries: ['EDTECH'],
          geo: 'VN',
        },
        {
          name: 'MFast',
          whatTheyDo:
            'Mạng lưới cộng tác viên phân phối sản phẩm tài chính tới vùng ngoài đô thị lớn.',
          industries: ['FINTECH'],
          geo: 'VN',
        },
        {
          name: 'Palexy',
          whatTheyDo:
            'Phân tích hành vi khách trong cửa hàng bằng camera và thị giác máy tính.',
          industries: ['AI', 'RETAILTECH'],
          geo: 'VN',
        },
        {
          name: 'Finhay',
          whatTheyDo:
            'Ứng dụng tích luỹ và đầu tư số tiền nhỏ cho người trẻ.',
          industries: ['FINTECH'],
          geo: 'VN',
        },
        {
          name: 'Azota',
          whatTheyDo:
            'Công cụ giao bài và chấm bài trực tuyến cho giáo viên phổ thông.',
          industries: ['EDTECH'],
          geo: 'VN',
        },
        {
          name: 'Vinmake',
          whatTheyDo:
            'Kết nối đơn hàng của thương hiệu với các nhà máy gia công trong nước.',
          industries: ['MANUFACTURING'],
          geo: 'VN',
        },
      ],
    },
    provenance: {
      sources: [
        {
          url: 'https://doventures.vc/en/about',
          fetchedAt: '2026-09-09',
          note: 'Trang giới thiệu chính thức, mục Investment Strategy',
        },
      ],
      lastVerifiedAt: '2026-09-09',
    },
  },

  {
    fundId: 'thinkzone-gmf1',
    name: 'ThinkZone, Global Minds Fund I',
    shortName: 'ThinkZone GMF I',
    slug: 'thinkzone-global-minds-fund-i',
    type: 'VC',
    vietnamPresence: 'OFFICE',
    website: 'https://www.thinkzone.vn',

    // Kênh nộp: biểu mẫu Lark do quỹ công bố. Địa chỉ email trên website bị che
    // bằng cơ chế chống thu thập tự động nên dùng biểu mẫu thay thế.
    publish: { status: 'PUBLISHED' },

    thesis: {
      capitalTypes: ['EQUITY'],
      // "startups from pre-seed to Series A" — blog chính thức của quỹ.
      stages: ['PRESEED', 'SEED', 'SERIES_A'],
      // Quỹ đầu tư bằng đồng Việt Nam theo Nghị định 38/2018, tập trung Việt Nam.
      geos: ['VN'],
      // "tech startups across various verticals" — không giới hạn ngành cụ thể.
      industries: [],
      // "cheque sizes up to US$3 million"; mức sàn không công bố.
      // Vehicle đang nhận hồ sơ. Quỹ công bố mức tối đa 1 triệu USD mỗi công ty.
      //
      // KHÔNG dùng con số 3 triệu USD của Fund II ở đây: hai vehicle khác
      // mandate, gộp lại thì founder gọi 2 triệu sẽ tưởng mình vẫn nằm trong
      // phạm vi của quỹ đang mở.
      ticket: {
        max: usd(1_000_000),
        basis: 'INITIAL_CHECK', confidence: 'HIGH', sourceType: 'OFFICIAL',
        hardFilter: true, verifiedAt: '2026-09-15',
      },
      businessModels: [],
      customerTypes: [],
      instruments: [],
    },

    process: {
      submitUrl: 'https://xfv1phagnc8.sg.larksuite.com/share/base/form/shrlgRP9afVQk68SCcNtLUT3eVg',
      notes: 'Nộp hồ sơ qua biểu mẫu trực tuyến của quỹ.',
    },

    content: {
      summary:
        // Con số 3 triệu USD là của Fund II (bản ghi riêng), không phải vehicle này.
        'Quỹ đầu tư giai đoạn sớm đặt tại Hà Nội, đầu tư vào startup công nghệ '
        + 'tại Việt Nam từ vòng pre-seed tới Series A, séc đầu tư tối đa 1 triệu USD '
        + 'mỗi công ty. Đầu tư bằng đồng Việt Nam theo khuôn khổ Nghị định 38/2018.',
      lookingFor: [
        'Startup công nghệ đa ngành tại thị trường Việt Nam',
        'Quỹ có chương trình tăng tốc riêng cho giai đoạn rất sớm',
      ],
    },

    provenance: {
      sources: [
        { url: 'https://www.thinkzone.vn/', fetchedAt: '2026-09-15', note: 'Global Minds Fund I: pre-seed đến Series A, tối đa 1 triệu USD mỗi công ty' },
        { url: 'https://xfv1phagnc8.sg.larksuite.com/share/base/form/shrlgRP9afVQk68SCcNtLUT3eVg', fetchedAt: '2026-09-15', note: 'Biểu mẫu nộp hồ sơ do quỹ công bố' },
        {
          url: 'https://www.thinkzone.vn/blog/the-inside-story-of-thinkzone-fund-ii',
          fetchedAt: '2026-09-09',
          note: 'Bài trên blog chính thức, nêu giai đoạn và quy mô séc của Fund II',
        },
      ],
      lastVerifiedAt: '2026-09-15',
    },
  },

  {
    // TÁCH KHỎI GLOBAL MINDS FUND I — hai vehicle, hai mandate.
    //
    // Giữ DRAFT vì chưa xác nhận được Fund II còn nhận hồ sơ mới hay đã đóng
    // và chuyển sang giải ngân từ GMF I. Hiển thị một quỹ không còn nhận hồ sơ
    // là để founder mất công chuẩn bị rồi gửi vào chỗ không ai đọc.
    fundId: 'thinkzone-fund-ii',
    name: 'ThinkZone Fund II',
    shortName: 'ThinkZone Fund II',
    slug: 'thinkzone-fund-ii',
    type: 'VC',
    vietnamPresence: 'OFFICE',
    vietnamEvidence: 'Quỹ Việt Nam, đầu tư bằng đồng Việt Nam theo Nghị định 38/2018.',
    website: 'https://www.thinkzone.vn',
    publish: { status: 'DRAFT' },

    thesis: {
      capitalTypes: ['EQUITY'],
      stages: ['PRESEED', 'SEED', 'SERIES_A'],
      industries: [],
      geos: ['VN'],
      businessModels: [],
      customerTypes: [],
      instruments: [],
      // "cheque sizes up to US$3 million" — blog chính thức về Fund II.
      ticket: {
        max: usd(3_000_000),
        basis: 'INITIAL_CHECK', confidence: 'HIGH', sourceType: 'OFFICIAL',
        hardFilter: true, verifiedAt: '2026-09-09',
      },
    },

    process: { submitUrl: null, notes: 'Chưa xác nhận kênh nộp hồ sơ cho vehicle này.' },

    content: {
      summary:
        'Quỹ thứ hai của ThinkZone, đầu tư vào startup công nghệ Việt Nam từ pre-seed đến Series A.',
    },

    provenance: {
      sources: [
        { url: 'https://www.thinkzone.vn/blog/the-inside-story-of-thinkzone-fund-ii', fetchedAt: '2026-09-09', note: 'Bài trên blog chính thức, nêu giai đoạn và quy mô séc của Fund II' },
      ],
      lastVerifiedAt: '2026-09-09',
    },
  },

  {
    fundId: 'touchstone-partners',
    name: 'Touchstone Partners',
    shortName: 'Touchstone',
    slug: 'touchstone-partners',
    type: 'VC',
    vietnamPresence: 'OFFICE',
    website: 'https://touchstone.vc',
    publish: { status: 'PUBLISHED' },

    thesis: {
      capitalTypes: ['EQUITY'],
      // Trang chính thức chỉ nói "early-stage tech startups", không liệt kê
      // ngành hay giai đoạn cụ thể → để trống, không suy từ danh mục đầu tư.
      industries: [],
      stages: [],
      geos: ['VN'],
      // "We write US$ 200k - 1 mil checks for early stage startups" — trang chủ.
      ticket: { min: usd(200_000), max: usd(1_000_000) },
      businessModels: [],
      customerTypes: [],
      instruments: [],
    },

    process: {
      submitUrl: 'https://touchstone.vc/contact-us',
      submitEmail: 'hello@touchstone.vc',
      notes: 'Trang có nút "Pitch Us" dẫn tới biểu mẫu liên hệ.',
    },

    content: {
      summary:
        'Quỹ đầu tư mạo hiểm đặt tại TP.HCM, đầu tư vào startup công nghệ giai '
        + 'đoạn sớm tại Việt Nam, séc từ 200 nghìn tới 1 triệu USD. Quỹ nhấn mạnh '
        + 'tiêu chí tác động xã hội và môi trường bên cạnh khả năng sinh lời.',
      lookingFor: [
        'Startup công nghệ giải quyết vấn đề nền tảng của Việt Nam',
        'Người sáng lập có năng lực và chính trực. Quỹ nêu rõ đây là tiêu chí hàng đầu',
      ],
      tips: [
        'Quỹ tự đặt câu hỏi "nếu startup này thành công thì tạo ra khác biệt gì cho '
        + 'Việt Nam". Hồ sơ nên trả lời được câu đó một cách cụ thể.',
        'Các lĩnh vực tác động quỹ công bố gồm y tế, tiểu thương, giáo dục, nông '
        + 'nghiệp và giảm phát thải.',
      ],
    },


    // Danh mục lấy từ trang danh mục chính thức của quỹ. Con số là TỔNG VÒNG
    // được công bố, không phải séc riêng của quỹ này — xem chú thích trong
    // chuanHoaDanhMuc() ở src/lib/fundModel.js.
    portfolio: {
      source: 'https://touchstone.vc/portfolio',
      verifiedAt: '2026-09-25',
      items: [
        {
          name: 'Selex Motors',
          whatTheyDo:
            'Xe máy điện chở hàng kèm mạng lưới trạm đổi pin cho tài xế giao hàng.',
          industries: ['MOBILITY', 'CLIMATE'],
          stage: 'SEED',
          geo: 'VN',
          roundDate: '2022-01',
          roundSize: usd(2100000),
          roundSource:
            'https://e27.co/vietnams-ev-startup-selex-motors-banks-us2-1m-touchstone-partners-20220120/',
          leadInvestor: true,
        },
        {
          name: 'Prep',
          whatTheyDo:
            'Nền tảng luyện thi ngoại ngữ trực tuyến có chấm chữa tự động.',
          industries: ['EDTECH', 'AI'],
          stage: 'SERIES_A',
          geo: 'VN',
          roundDate: '2024-05',
          roundSize: usd(7000000),
          roundSource:
            'https://vir.com.vn/language-platform-prep-raises-7-million-in-series-a-funding-110821.html',
          leadInvestor: false,
        },
        {
          name: 'Medigo',
          whatTheyDo:
            'Đặt thuốc và tư vấn dược sĩ 24/7 qua ứng dụng.',
          industries: ['HEALTHTECH'],
          geo: 'VN',
        },
        {
          name: 'Urbox',
          whatTheyDo:
            'Quà tặng số và chương trình khách hàng thân thiết cho doanh nghiệp.',
          industries: ['MARTECH'],
          geo: 'VN',
        },
        {
          name: 'Alterno',
          whatTheyDo:
            'Pin lưu trữ nhiệt bằng cát, thay nhiên liệu đốt trong sấy nông sản.',
          industries: ['CLIMATE', 'ENERGY'],
          geo: 'VN',
        },
        {
          name: 'Enfarm',
          whatTheyDo:
            'Cảm biến đo dinh dưỡng đất, khuyến nghị bón phân theo từng vườn.',
          industries: ['AGRITECH', 'AI'],
          geo: 'VN',
        },
        {
          name: 'Otrafy',
          whatTheyDo:
            'Phần mềm quản lý hồ sơ nhà cung cấp và truy xuất nguồn gốc thực phẩm.',
          industries: ['SAAS_B2B', 'FOODBEV'],
          geo: 'VN',
        },
        {
          name: 'Piktina',
          whatTheyDo:
            'Sàn mua bán quần áo đã qua sử dụng.',
          industries: ['ECOMMERCE'],
          geo: 'VN',
        },
      ],
    },
    provenance: {
      sources: [
        {
          url: 'https://touchstone.vc/',
          fetchedAt: '2026-09-09',
          note: 'Trang chủ chính thức, mục Investment Thesis và Contact',
        },
      ],
      lastVerifiedAt: '2026-09-09',
    },
  },

  {
    fundId: 'genesia-ventures',
    name: 'Genesia Ventures',
    shortName: 'Genesia',
    slug: 'genesia-ventures',
    type: 'VC',
    vietnamPresence: 'OFFICE',
    website: 'https://www.genesiaventures.com',
    publish: { status: 'PUBLISHED' },

    thesis: {
      capitalTypes: ['EQUITY'],
      // Trang chủ: "dedicated to seed and early stage investments in innovative
      // digital startups in Asia". Thông cáo quỹ thứ ba trên chính trang của họ
      // ghi "pre-seed and seed stage startups".
      stages: ['PRESEED', 'SEED'],
      // Có văn phòng tại TP.HCM; phạm vi công bố gồm Nhật Bản, Đông Nam Á, Ấn Độ.
      geos: ['VN', 'SEA'],
      // Không giới hạn ngành cụ thể, chỉ nêu "digital startups" → để trống.
      industries: [],
      // Không công bố quy mô séc trên trang chính thức.
      businessModels: [],
      customerTypes: [],
      instruments: [],
    },

    process: {
      submitUrl: 'https://www.genesiaventures.com/en/contact-en/',
      notes: 'Trang liên hệ chính thức, có biểu mẫu.',
    },

    content: {
      summary:
        'Quỹ đầu tư độc lập đặt tại Tokyo, chuyên vòng pre-seed và seed cho '
        + 'startup công nghệ số tại châu Á. Có văn phòng tại TP.HCM và đã đầu tư '
        + 'vào nhiều doanh nghiệp Việt Nam.',
      lookingFor: [
        'Startup công nghệ số ở giai đoạn pre-seed tới seed',
        'Quỹ không giới hạn ngành cụ thể, nhấn mạnh chuyển đổi số và ngành bền vững',
      ],
      tips: [
        'Quỹ không công bố quy mô séc trên trang chính thức. Hỏi thẳng trong buổi đầu.',
        'Có mặt tại Việt Nam từ sớm nên hiểu bối cảnh trong nước hơn phần lớn quỹ khu vực.',
      ],
    },


    // Danh mục lấy từ trang danh mục chính thức của quỹ. Con số là TỔNG VÒNG
    // được công bố, không phải séc riêng của quỹ này — xem chú thích trong
    // chuanHoaDanhMuc() ở src/lib/fundModel.js.
    portfolio: {
      source: 'https://www.genesiaventures.com/en/partners/',
      verifiedAt: '2026-09-24',
      note: 'Mô tả từng doanh nghiệp lấy theo đúng cách quỹ ghi trên trang danh mục.',
      items: [
        {
          name: 'Fundiin',
          whatTheyDo:
            'Dịch vụ mua trước trả sau cho thương mại điện tử và bán lẻ.',
          industries: ['FINTECH'],
          geo: 'VN',
        },
        {
          name: 'Kamereo',
          whatTheyDo:
            'Bán sỉ nguyên liệu thực phẩm cho nhà hàng, quán ăn và doanh nghiệp.',
          industries: ['FOODBEV', 'ECOMMERCE'],
          geo: 'VN',
        },
        {
          name: 'BuyMed',
          whatTheyDo:
            'Sàn đặt hàng dược phẩm cho nhà thuốc.',
          industries: ['HEALTHTECH', 'ECOMMERCE'],
          geo: 'VN',
        },
        {
          name: 'Selly',
          whatTheyDo:
            'Bán hàng qua mạng lưới cộng tác viên trên mạng xã hội.',
          industries: ['ECOMMERCE'],
          geo: 'VN',
        },
        {
          name: 'Rootopia',
          whatTheyDo:
            'Tài chính cho giáo dục, bắt đầu từ dịch vụ cho vay học phí.',
          industries: ['FINTECH', 'EDTECH'],
          geo: 'VN',
        },
        {
          name: 'eDoctor',
          whatTheyDo:
            'Khám sức khoẻ cho cá nhân và cho nhân viên doanh nghiệp.',
          industries: ['HEALTHTECH'],
          geo: 'VN',
        },
        {
          name: 'MVillage',
          whatTheyDo:
            'Chuỗi chỗ ở cho thuê dài ngày trong đô thị.',
          industries: ['PROPTECH'],
          geo: 'VN',
        },
        {
          name: 'Manabie',
          whatTheyDo:
            'Học trực tuyến kết hợp lớp học trực tiếp cho học sinh phổ thông.',
          industries: ['EDTECH'],
          geo: 'SEA',
        },
      ],
    },
    provenance: {
      sources: [
        {
          url: 'https://www.genesiaventures.com/en/top-en/',
          fetchedAt: '2026-09-11',
          note: 'Trang chủ bản tiếng Anh, mục STAGE và INVESTMENT',
        },
        {
          url: 'https://www.genesiaventures.com/en/4thfund-finalclose-eng/',
          fetchedAt: '2026-09-11',
          note: 'Thông cáo quỹ thứ tư, nêu rõ tập trung vòng seed và các văn phòng',
        },
      ],
      lastVerifiedAt: '2026-09-11',
    },
  },

  {
    fundId: 'nextrans',
    name: 'Nextrans',
    shortName: 'Nextrans',
    slug: 'nextrans',
    type: 'VC',
    vietnamPresence: 'OFFICE',
    website: 'https://www.nextrans.vn',

    // CHƯA HIỂN THỊ. Trang chính thức chỉ có phần giới thiệu và danh mục, không
    // công bố giai đoạn, ngành, quy mô séc hay kênh nộp hồ sơ. Các nguồn bên thứ
    // ba có nêu con số nhưng mâu thuẫn nhau, nên không dùng.
    publish: { status: 'DRAFT' },

    thesis: {
      capitalTypes: ['EQUITY'],
      // Trang chính thức: bắt đầu đầu tư vào startup Việt Nam từ 2015, văn phòng Hà Nội.
      geos: ['VN'],
      stages: [],
      industries: [],
      businessModels: [],
      customerTypes: [],
      instruments: [],
    },

    process: {
      submitUrl: null,
      notes: 'Cần xác nhận kênh nộp hồ sơ chính thức trước khi hiển thị.',
    },

    content: {
      summary:
        'Quỹ đầu tư giai đoạn sớm gốc Hàn Quốc, bắt đầu đầu tư vào startup Việt '
        + 'Nam từ năm 2015, văn phòng tại Hà Nội.',
      tips: [
        'Trang chính thức không công bố giai đoạn và quy mô séc. Những con số lưu '
        + 'truyền trên các trang tổng hợp mâu thuẫn nhau, nên hỏi thẳng quỹ.',
      ],
    },


    // Danh mục lấy từ trang danh mục chính thức của quỹ. Con số là TỔNG VÒNG
    // được công bố, không phải séc riêng của quỹ này — xem chú thích trong
    // chuanHoaDanhMuc() ở src/lib/fundModel.js.
    portfolio: {
      source: 'https://www.nextrans.vn/portfolio',
      verifiedAt: '2026-09-25',
      items: [
        {
          name: 'Base.vn',
          whatTheyDo:
            'Bộ phần mềm quản trị công việc và nhân sự cho doanh nghiệp.',
          industries: ['SAAS_B2B'],
          geo: 'VN',
          outcome: 'ACQUIRED',
          outcomeNote: 'FPT mua cổ phần chi phối tháng 5/2021.',
        },
        {
          name: 'Tiki',
          whatTheyDo:
            'Sàn thương mại điện tử tự vận hành kho và giao hàng.',
          industries: ['ECOMMERCE', 'LOGISTICS'],
          geo: 'VN',
        },
        {
          name: 'TopCV',
          whatTheyDo:
            'Nền tảng hồ sơ ứng tuyển và tuyển dụng trực tuyến.',
          industries: ['HRTECH'],
          geo: 'VN',
        },
        {
          name: 'BuyMed',
          whatTheyDo:
            'Sàn phân phối dược phẩm cho nhà thuốc.',
          industries: ['HEALTHTECH', 'ECOMMERCE'],
          geo: 'VN',
        },
        {
          name: 'bTaskee',
          whatTheyDo:
            'Ứng dụng đặt người giúp việc và dịch vụ gia đình theo giờ.',
          industries: ['OTHER'],
          geo: 'VN',
        },
        {
          name: 'EcoTruck',
          whatTheyDo:
            'Sàn kết nối chủ hàng với nhà xe, giảm chuyến chạy rỗng.',
          industries: ['LOGISTICS'],
          geo: 'VN',
        },
        {
          name: 'Infina',
          whatTheyDo:
            'Ứng dụng đầu tư chứng chỉ quỹ và tích luỹ cho nhà đầu tư cá nhân.',
          industries: ['FINTECH'],
          geo: 'VN',
        },
        {
          name: 'Citics',
          whatTheyDo:
            'Dữ liệu và định giá bất động sản phục vụ ngân hàng, môi giới.',
          industries: ['PROPTECH'],
          geo: 'VN',
        },
      ],
    },
    provenance: {
      sources: [
        {
          url: 'https://www.nextrans.vn/',
          fetchedAt: '2026-09-11',
          note: 'Trang chủ chính thức, chỉ có phần giới thiệu và danh mục đầu tư',
        },
      ],
      lastVerifiedAt: '2026-09-11',
    },
  },

  {
    fundId: 'beacon-fund',
    name: 'Beacon Fund',
    shortName: 'Beacon',
    slug: 'beacon-fund',
    type: 'LENDER', // Chỉ cấp vốn vay / hoàn trả, không lấy cổ phần.
    vietnamPresence: 'OFFICE',
    website: 'https://beaconfund.com',
    publish: { status: 'PUBLISHED' },

    thesis: {
      // Quỹ cho vay tư nhân — KHÔNG lấy cổ phần. Đây là quỹ đầu tiên trong
      // danh sách thuộc loại vốn không pha loãng.
      capitalTypes: ['DEBT'],
      // "sector-agnostic investment fund" — trang Cách chúng tôi đầu tư.
      industries: [],
      // Không phải vòng gọi vốn theo nghĩa startup. Quỹ nhắm doanh nghiệp vừa
      // và nhỏ đã có dòng tiền, nên để trống thay vì gán bậc giai đoạn startup.
      stages: [],
      geos: ['VN', 'SEA'],
      businessModels: [],
      customerTypes: [],
      instruments: [],
      requiresRevenue: true,

      hardRequirements: [
        {
          code: 'DO_PHU_NU_LANH_DAO',
          field: 'identity.womenLed',
          op: 'isTrue',
          label:
            'Doanh nghiệp do phụ nữ sở hữu hoặc lãnh đạo, hoặc phục vụ chủ yếu '
            + 'phụ nữ và trẻ em gái',
        },
        {
          code: 'BA_NAM_BAO_CAO',
          field: 'finance.yearsOfFinancials',
          op: 'gte',
          value: 3,
          label: 'Có ít nhất 3 năm báo cáo tài chính lập theo chuẩn mực kế toán Việt Nam',
        },
      ],
    },

    process: {
      submitUrl: 'https://beaconfund.com/contact/',
      submitEmail: 'info@beaconfund.com',
      notes: 'Trang Cách chúng tôi đầu tư có nút "Apply for Our Funding" dẫn tới biểu mẫu.',
    },

    content: {
      summary:
        'Quỹ cho vay tư nhân đặt tại TP.HCM, cấp khoản vay trung hạn cho doanh '
        + 'nghiệp vừa và nhỏ do phụ nữ lãnh đạo. Không lấy cổ phần và không yêu '
        + 'cầu thế chấp bất động sản.',
      lookingFor: [
        'Doanh nghiệp do phụ nữ sở hữu hoặc lãnh đạo, hoặc phục vụ chủ yếu phụ nữ và trẻ em gái',
        'Doanh nghiệp đã có dòng tiền dương, tăng trưởng đều, không cần mô hình tăng trưởng bùng nổ',
        'Có tác động xã hội đo lường được bên cạnh yếu tố giới',
      ],
      tips: [
        'Quỹ tự mô tả nhắm vào "khoảng giữa còn thiếu": doanh nghiệp quá lớn cho '
        + 'tài chính vi mô, quá nhỏ cho quỹ đầu tư tư nhân, không hợp mô hình '
        + 'tăng trưởng của vốn mạo hiểm, và thiếu tài sản thế chấp để vay ngân hàng.',
        'Nếu doanh nghiệp của bạn có doanh thu ổn định nhưng bị quỹ mạo hiểm từ '
        + 'chối vì thị trường không đủ lớn, đây là hướng đáng xem.',
      ],
    },


    // Danh mục lấy từ trang danh mục chính thức của quỹ. Con số là TỔNG VÒNG
    // được công bố, không phải séc riêng của quỹ này — xem chú thích trong
    // chuanHoaDanhMuc() ở src/lib/fundModel.js.
    portfolio: {
      source: 'https://beaconfund.com/our-portfolio/',
      verifiedAt: '2026-09-26',
      note: 'Beacon Fund cấp vốn vay và vốn hoàn trả chứ không đổi cổ phần, “danh mục” ở đây là các doanh nghiệp đã được cấp vốn. Mô tả lấy theo đúng cách quỹ ghi trên trang.',
      items: [
        {
          name: 'MindX Technology School',
          whatTheyDo:
            'Trường dạy lập trình cho học sinh lớp 5-12 và người đi làm trẻ.',
          industries: ['EDTECH'],
          geo: 'VN',
        },
        {
          name: 'CAS Energy',
          whatTheyDo:
            'Năng lượng tái tạo và nông nghiệp thông minh thích ứng biến đổi khí hậu.',
          industries: ['ENERGY', 'AGRITECH'],
          geo: 'VN',
        },
        {
          name: 'Hoa Nắng Organic',
          whatTheyDo:
            'Doanh nghiệp lúa gạo khép kín, đạt chứng nhận hữu cơ USDA và EU.',
          industries: ['AGRITECH', 'FOODBEV'],
          geo: 'VN',
        },
        {
          name: 'Hoa Sen Việt Group',
          whatTheyDo:
            'Bán lẻ mỹ phẩm và sản phẩm chăm sóc da nhiều thương hiệu.',
          industries: ['RETAILTECH'],
          geo: 'VN',
        },
        {
          name: 'Lotus Group',
          whatTheyDo:
            'Doanh nghiệp thực phẩm đồ uống vận hành chuỗi nhà hàng Nhật.',
          industries: ['FOODBEV'],
          geo: 'VN',
        },
        {
          name: 'TIMEC',
          whatTheyDo:
            'Cơ sở y tế cung cấp dịch vụ khám chữa bệnh chi phí vừa phải.',
          industries: ['HEALTHTECH'],
          geo: 'VN',
        },
        {
          name: 'Komarc',
          whatTheyDo:
            'Sản xuất đồ chơi và giáo cụ cho các thương hiệu toàn cầu.',
          industries: ['MANUFACTURING'],
          geo: 'VN',
        },
        {
          name: 'Bồ Câu Services',
          whatTheyDo:
            'Hạ tầng viễn thông, dẫn đầu thị trường trong nước.',
          industries: ['OTHER'],
          geo: 'VN',
        },
      ],
    },
    provenance: {
      sources: [
        {
          url: 'https://beaconfund.com/',
          fetchedAt: '2026-09-11',
          note: 'Trang chủ chính thức, mô tả sản phẩm cho vay và đối tượng',
        },
        {
          url: 'https://beaconfund.com/how-we-invest',
          fetchedAt: '2026-09-11',
          note: 'Trang Cách chúng tôi đầu tư, đối tượng, địa bàn, kênh nộp hồ sơ',
        },
        {
          url: 'https://beaconfund.com/faqs/',
          fetchedAt: '2026-09-11',
          note: 'Trang hỏi đáp, yêu cầu 3 năm báo cáo tài chính, kênh liên hệ',
        },
      ],
      lastVerifiedAt: '2026-09-11',
    },
  },

  {
    fundId: 'ascend-vietnam-ventures',
    name: 'Ascend Vietnam Ventures',
    shortName: 'AVV',
    slug: 'ascend-vietnam-ventures',
    type: 'VC',
    vietnamPresence: 'OFFICE',
    website: 'https://avv.co',
    publish: { status: 'PUBLISHED' },

    thesis: {
      capitalTypes: ['EQUITY'],
      // Trang FAQ nêu giai đoạn từ ý tưởng tới seed.
      stages: ['IDEA', 'PRESEED', 'SEED'],
      industries: [
        'AI', 'FINTECH', 'EDTECH', 'SAAS_B2B', 'ECOMMERCE',
        'HEALTHTECH', 'AGRITECH', 'GAMING', 'WEB3',
      ],
      geos: ['VN', 'SEA'],
      // Séc ban đầu. Phần rót thêm ở vòng sau tới 5 triệu USD không tính vào
      // đây — công cụ khớp theo số tiền vòng này, không phải tổng cả đời.
      ticket: { min: usd(250_000), max: usd(2_000_000) },
      businessModels: [],
      customerTypes: [],
      instruments: [],
    },

    process: {
      submitUrl: 'https://avv.co/connect',
      notes: 'Trang Pitch Us có biểu mẫu và chỗ tải pitch deck.',
    },

    content: {
      summary:
        'Quỹ đầu tư giai đoạn sớm gốc Việt Nam, séc ban đầu từ 250 nghìn tới '
        + '2 triệu USD, có thể rót thêm tới 5 triệu USD ở vòng sau.',
      tips: [
        'Con số 5 triệu USD hay được nhắc tới là mức rót thêm cộng dồn, không '
        + 'phải séc của một vòng.',
      ],
    },


    // Danh mục lấy từ trang danh mục chính thức của quỹ. Con số là TỔNG VÒNG
    // được công bố, không phải séc riêng của quỹ này — xem chú thích trong
    // chuanHoaDanhMuc() ở src/lib/fundModel.js.
    portfolio: {
      source: 'https://avv.co/portfolio',
      verifiedAt: '2026-09-25',
      note: 'Vòng Series A của TechCoop gồm cả phần vốn vay bên cạnh vốn cổ phần.',
      items: [
        {
          name: 'TechCoop',
          whatTheyDo:
            'Nền tảng vốn, vật tư và đầu ra cho hợp tác xã nông nghiệp.',
          industries: ['AGRITECH', 'FINTECH'],
          stage: 'SERIES_A',
          geo: 'VN',
          roundDate: '2025-02',
          roundSize: usd(70000000),
          roundSource:
            'https://en.vneconomy.vn/vietnamese-agri-tech-startup-secures-70-mln-in-series-a-financing.htm',
          leadInvestor: true,
        },
        {
          name: 'Sky Mavis',
          whatTheyDo:
            'Hạ tầng game blockchain, đơn vị tạo ra Axie Infinity.',
          industries: ['WEB3', 'GAMING'],
          geo: 'VN',
        },
        {
          name: 'Trusting Social',
          whatTheyDo:
            'Chấm điểm tín dụng bằng dữ liệu thay thế cho người chưa có lịch sử ngân hàng.',
          industries: ['FINTECH', 'AI'],
          geo: 'VN',
        },
        {
          name: 'Turing',
          whatTheyDo:
            'Tuyển và vận hành đội kỹ sư phần mềm từ xa cho doanh nghiệp toàn cầu.',
          industries: ['HRTECH', 'AI'],
          geo: 'GLOBAL',
        },
        {
          name: 'Obello',
          whatTheyDo:
            'Công cụ AI tạo ấn phẩm thiết kế đúng bộ nhận diện thương hiệu.',
          industries: ['AI', 'MARTECH'],
          geo: 'VN',
        },
        {
          name: 'Vaudit',
          whatTheyDo:
            'Kiểm tra chi tiêu quảng cáo số theo thời gian thực bằng AI.',
          industries: ['MARTECH', 'AI'],
          geo: 'VN',
        },
      ],
    },
    provenance: {
      sources: [
        { url: 'https://avv.co/about', fetchedAt: '2026-09-11', note: 'Trang giới thiệu' },
        { url: 'https://avv.co/faq', fetchedAt: '2026-09-11', note: 'Trang hỏi đáp, giai đoạn, ngành, quy mô séc' },
        { url: 'https://avv.co/connect', fetchedAt: '2026-09-11', note: 'Kênh nộp hồ sơ chính thức' },
      ],
      lastVerifiedAt: '2026-09-11',
    },
  },

  {
    fundId: 'antler-vietnam',
    name: 'Antler Vietnam',
    shortName: 'Antler',
    slug: 'antler-vietnam',
    type: 'ACCELERATOR',
    vietnamPresence: 'OFFICE',
    website: 'https://www.antler.co/location/vietnam',
    publish: { status: 'PUBLISHED' },

    thesis: {
      capitalTypes: ['EQUITY'],
      // Chương trình residency nhận người từ giai đoạn ý tưởng. Các mốc sau đó
      // là rót thêm, không phải cửa vào.
      stages: ['IDEA', 'PROTOTYPE', 'PRESEED'],
      industries: [],
      geos: ['VN', 'SEA'],
      // 120 nghìn USD đổi 12% qua SAFE — đây là khoản Antler công bố.
      // KHÔNG cộng phần matching và phần rót thêm vào thành một con số: chúng
      // là các khoản riêng có điều kiện riêng.
      ticket: { min: usd(120_000), max: usd(120_000) },
      businessModels: [],
      customerTypes: [],
      instruments: ['SAFE'],
    },

    process: {
      submitUrl: 'https://www.antler.co/pitch',
      notes: 'Nhận hồ sơ liên tục, không theo đợt.',
    },

    content: {
      summary:
        'Chương trình residency tại TP.HCM dành cho người sáng lập giai đoạn rất '
        + 'sớm, kể cả khi chưa có đồng sáng lập hay sản phẩm. Đầu tư 120 nghìn USD '
        + 'đổi 12% cổ phần qua SAFE.',
      tips: [
        'Đây là chương trình cư trú, thường yêu cầu có mặt tại TP.HCM trong vài '
        + 'tuần. Cân nhắc yếu tố thời gian trước khi nộp.',
        'Ngoài khoản ban đầu còn có phần đầu tư đối ứng và rót thêm ở vòng sau, '
        + 'nhưng đó là các khoản riêng với điều kiện riêng.',
      ],
    },


    // Danh mục lấy từ trang danh mục chính thức của quỹ. Con số là TỔNG VÒNG
    // được công bố, không phải séc riêng của quỹ này — xem chú thích trong
    // chuanHoaDanhMuc() ở src/lib/fundModel.js.
    portfolio: {
      source: 'https://www.antler.co/location/vietnam',
      verifiedAt: '2026-09-26',
      note: 'Antler đầu tư từ lúc đội ngũ mới thành lập nên phần lớn danh mục là doanh nghiệp rất sớm. Mô tả lấy theo đúng cách quỹ ghi trên trang.',
      items: [
        {
          name: 'Alterno',
          whatTheyDo:
            'Lưu trữ nhiệt và điện bằng cát, có bằng sáng chế tại Mỹ.',
          industries: ['CLIMATE', 'ENERGY'],
          geo: 'VN',
          roundDate: '2023',
        },
        {
          name: 'Buyo',
          whatTheyDo:
            'Biến phụ phẩm sinh học bỏ đi thành vật liệu tự phân huỷ hoàn toàn.',
          industries: ['CLIMATE', 'MATERIALS'],
          geo: 'VN',
          roundDate: '2022',
        },
        {
          name: 'Wareflex',
          whatTheyDo:
            'Nền tảng B2B đặt dịch vụ kho bãi và vận chuyển.',
          industries: ['LOGISTICS'],
          geo: 'VN',
          roundDate: '2022',
        },
        {
          name: 'Inflow',
          whatTheyDo:
            'Nền tảng chuỗi cung ứng và sản xuất cho thương hiệu thời trang.',
          industries: ['MANUFACTURING', 'SAAS_B2B'],
          geo: 'VN',
          roundDate: '2022',
        },
        {
          name: 'Aircity',
          whatTheyDo:
            'Phần mềm quản lý vận hành toà nhà và bất động sản cho thuê.',
          industries: ['PROPTECH'],
          geo: 'VN',
          roundDate: '2022',
        },
        {
          name: 'Neoloka',
          whatTheyDo:
            'Mạng lưới điểm dịch vụ tại chỗ cho giao hàng chặng cuối.',
          industries: ['LOGISTICS'],
          geo: 'VN',
          roundDate: '2022',
        },
        {
          name: 'Tribee',
          whatTheyDo:
            'Huấn luyện đội bán hàng dựa trên dữ liệu, có hỗ trợ AI.',
          industries: ['SAAS_B2B', 'AI'],
          geo: 'VN',
          roundDate: '2021',
        },
        {
          name: 'Megasop',
          whatTheyDo:
            'Nền tảng số cho mạng lưới phân phối và bán lẻ hiện đại.',
          industries: ['SAAS_B2B', 'RETAILTECH'],
          geo: 'VN',
          roundDate: '2023',
        },
      ],
    },
    provenance: {
      sources: [
        { url: 'https://www.antler.co/location/vietnam', fetchedAt: '2026-09-11', note: 'Trang chương trình Việt Nam' },
        { url: 'https://www.antler.co/location/vietnam#faq', fetchedAt: '2026-09-11', note: 'Hỏi đáp. Điều kiện và quy mô đầu tư' },
        { url: 'https://www.antler.co/pitch', fetchedAt: '2026-09-11', note: 'Kênh nộp hồ sơ chính thức' },
      ],
      lastVerifiedAt: '2026-09-11',
    },
  },

  {
    fundId: 'vinacapital-ventures',
    name: 'VinaCapital Ventures',
    shortName: 'VinaCapital Ventures',
    slug: 'vinacapital-ventures',
    type: 'VC',
    vietnamPresence: 'OFFICE',
    website: 'https://ventures.vinacapital.com',

    // CHƯA HIỂN THỊ. Quỹ vận hành nhiều vehicle với khẩu vị khác nhau — Fund 2
    // giai đoạn sớm, Fund 3 giai đoạn tăng trưởng séc 3–10 triệu USD. Gộp làm
    // một bản ghi sẽ cho ra khoảng séc sai với cả hai. Cần tách thành hai bản
    // ghi trước khi hiển thị.
    publish: { status: 'DRAFT' },

    thesis: {
      capitalTypes: ['EQUITY'],
      stages: [],
      industries: [],
      geos: ['VN', 'SEA'],
      businessModels: [],
      customerTypes: [],
      instruments: [],
    },

    process: {
      submitUrl: 'https://ventures.vinacapital.com/contact-us/',
      submitEmail: 'investment.vc@vinacapital.com',
      notes: 'Cần tách Fund 2 và Fund 3 trước khi hiển thị.',
    },

    content: {
      summary:
        'Nền tảng đầu tư công nghệ tại TP.HCM thuộc VinaCapital, tập trung vào '
        + 'startup công nghệ liên quan tới Việt Nam.',
    },


    // Danh mục lấy từ trang danh mục chính thức của quỹ. Con số là TỔNG VÒNG
    // được công bố, không phải séc riêng của quỹ này — xem chú thích trong
    // chuanHoaDanhMuc() ở src/lib/fundModel.js.
    portfolio: {
      source: 'https://ventures.vinacapital.com',
      verifiedAt: '2026-09-27',
      note: 'Quỹ không công bố số tiền của từng thương vụ.',
      items: [
        {
          name: 'Chicilon Media',
          whatTheyDo:
            'Mạng quảng cáo màn hình trong thang máy chung cư và toà văn phòng.',
          industries: ['MEDIA', 'MARTECH'],
          geo: 'VN',
        },
        {
          name: 'Wee Digital',
          whatTheyDo:
            'Xác thực khuôn mặt và thanh toán sinh trắc học cho ngân hàng.',
          industries: ['FINTECH', 'AI'],
          geo: 'VN',
        },
        {
          name: 'Global Care',
          whatTheyDo:
            'Nền tảng phân phối và xử lý bồi thường bảo hiểm số.',
          industries: ['INSURTECH'],
          geo: 'VN',
        },
        {
          name: 'Urbox',
          whatTheyDo:
            'Quà tặng số và chương trình khách hàng thân thiết cho doanh nghiệp.',
          industries: ['MARTECH'],
          geo: 'VN',
        },
        {
          name: 'Validus',
          whatTheyDo:
            'Cho vay vốn lưu động cho doanh nghiệp nhỏ và vừa.',
          industries: ['FINTECH'],
          geo: 'SEA',
        },
        {
          name: 'Coinhako',
          whatTheyDo:
            'Sàn giao dịch tài sản số tại Đông Nam Á.',
          industries: ['WEB3', 'FINTECH'],
          geo: 'SEA',
        },
        {
          name: 'Protos Labs',
          whatTheyDo:
            'Định lượng rủi ro an ninh mạng cho doanh nghiệp và công ty bảo hiểm.',
          industries: ['CYBERSECURITY'],
          geo: 'SEA',
        },
        {
          name: 'Sygnum',
          whatTheyDo:
            'Ngân hàng tài sản số có giấy phép tại Thuỵ Sĩ và Singapore.',
          industries: ['FINTECH', 'WEB3'],
          geo: 'GLOBAL',
        },
      ],
    },
    provenance: {
      sources: [
        { url: 'https://ventures.vinacapital.com/about-us/', fetchedAt: '2026-09-11', note: 'Trang giới thiệu và tiêu chí đánh giá' },
        { url: 'https://ventures.vinacapital.com/history/', fetchedAt: '2026-09-11', note: 'Lịch sử các quỹ, Fund 2 giai đoạn sớm, Fund 3 tăng trưởng' },
        { url: 'https://ventures.vinacapital.com/contact-us/', fetchedAt: '2026-09-11', note: 'Kênh nộp hồ sơ chính thức' },
      ],
      lastVerifiedAt: '2026-09-11',
    },
  },

  {
    fundId: '500-global-vietnam',
    name: '500 Global, Việt Nam và Đông Nam Á',
    shortName: '500 Global',
    slug: '500-global-vietnam',
    type: 'VC',
    vietnamPresence: 'OFFICE',
    website: 'https://vietnam.500.co',

    publish: { status: 'PUBLISHED' },

    thesis: {
      capitalTypes: ['EQUITY'],
      stages: ['PRESEED', 'SEED'],
      industries: ['FINTECH', 'SAAS_B2B', 'AGRITECH', 'FOODBEV', 'HEALTHTECH', 'CLIMATE'],
      geos: ['VN', 'SEA'],
      businessModels: [],
      customerTypes: [],
      instruments: [],
      // Quỹ công bố séc early-stage 250–500 nghìn USD.
      ticket: {
        min: usd(250_000), max: usd(500_000),
        basis: 'INITIAL_CHECK', confidence: 'HIGH', sourceType: 'OFFICIAL',
        hardFilter: true, verifiedAt: '2026-09-15',
      },
    },

    process: {
      submitUrl: 'https://sea.500.co/get-in-touch/pitch-for-investment',
      notes: 'Nộp hồ sơ qua biểu mẫu pitch của chương trình Đông Nam Á.',
    },

    content: {
      summary:
        'Nền tảng đầu tư toàn cầu có microsite riêng cho Việt Nam và quỹ dành '
        + 'cho khu vực Đông Nam Á.',
    },


    // Danh mục lấy từ trang danh mục chính thức của quỹ. Con số là TỔNG VÒNG
    // được công bố, không phải séc riêng của quỹ này — xem chú thích trong
    // chuanHoaDanhMuc() ở src/lib/fundModel.js.
    portfolio: {
      source: 'https://vietnam.500.co',
      verifiedAt: '2026-09-24',
      note: 'Quỹ nêu tên một số doanh nghiệp đã vượt mốc định giá 1 tỷ USD; danh mục Đông Nam Á của quỹ hơn 300 doanh nghiệp, phần lớn không công bố.',
      items: [
        {
          name: 'Grab',
          whatTheyDo:
            'Siêu ứng dụng gọi xe, giao đồ ăn và thanh toán tại Đông Nam Á.',
          industries: ['MOBILITY', 'LOGISTICS'],
          geo: 'SEA',
          outcome: 'IPO',
          outcomeNote: 'Niêm yết trên sàn Nasdaq năm 2021.',
        },
        {
          name: 'Bukalapak',
          whatTheyDo:
            'Sàn thương mại điện tử và dịch vụ cho tiệm tạp hoá tại Indonesia.',
          industries: ['ECOMMERCE'],
          geo: 'SEA',
          outcome: 'IPO',
          outcomeNote: 'Niêm yết trên sàn chứng khoán Indonesia năm 2021.',
        },
        {
          name: 'Carousell',
          whatTheyDo:
            'Sàn rao vặt mua bán đồ đã qua sử dụng tại Đông Nam Á.',
          industries: ['ECOMMERCE'],
          geo: 'SEA',
        },
        {
          name: 'Carsome',
          whatTheyDo:
            'Nền tảng mua bán và kiểm định ô tô đã qua sử dụng.',
          industries: ['ECOMMERCE', 'MOBILITY'],
          geo: 'SEA',
        },
        {
          name: 'FinAccel',
          whatTheyDo:
            'Công ty mẹ của Kredivo, cho vay tiêu dùng và mua trước trả sau.',
          industries: ['FINTECH'],
          geo: 'SEA',
        },
      ],
    },
    provenance: {
      sources: [
        { url: 'https://sea.500.co/get-in-touch/pitch-for-investment', fetchedAt: '2026-09-15', note: 'Biểu mẫu nhận pitch của 500 Southeast Asia' },
        { url: 'https://500.co/southeast-asia', fetchedAt: '2026-09-15', note: 'Trang chương trình Đông Nam Á: séc 250–500 nghìn USD, các nhóm ngành ưu tiên' },
        { url: 'https://vietnam.500.co/', fetchedAt: '2026-09-11', note: 'Microsite Việt Nam' },
        { url: 'https://500.co/sea', fetchedAt: '2026-09-11', note: 'Trang khu vực Đông Nam Á' },
      ],
      lastVerifiedAt: '2026-09-15',
    },
  },

  {
    fundId: 'hcm-vif',
    name: 'Quỹ đầu tư mạo hiểm TP. Hồ Chí Minh (HCM VIF)',
    shortName: 'HCM VIF',
    slug: 'hcm-vif',
    type: 'GOV',
    vietnamPresence: 'OFFICE',
    website: 'https://dost.hochiminhcity.gov.vn',

    // CHƯA HIỂN THỊ. Quỹ đã vận hành nhưng chưa thấy cổng nộp hồ sơ dành cho
    // startup và tiêu chí quy mô đầu tư công khai ổn định.
    publish: { status: 'DRAFT' },

    thesis: {
      capitalTypes: ['EQUITY'],
      stages: ['PRESEED', 'SEED', 'SERIES_A'],
      industries: ['AI', 'SEMICONDUCTOR', 'BIOTECH', 'MATERIALS', 'ENERGY'],
      geos: ['VN-HCM', 'VN'],
      businessModels: [],
      customerTypes: [],
      instruments: [],
      hardRequirements: [
        {
          code: 'DIA_BAN_HCM',
          field: 'identity.city',
          op: 'in',
          value: ['HCM'],
          label: 'Ưu tiên doanh nghiệp hoạt động tại TP. Hồ Chí Minh',
        },
      ],
    },

    process: {
      submitUrl: null,
      notes: 'Chưa xác nhận được cổng nộp hồ sơ dành riêng cho startup.',
    },

    content: {
      summary:
        'Quỹ đầu tư mạo hiểm của TP. Hồ Chí Minh, hướng vào doanh nghiệp đổi mới '
        + 'sáng tạo trong các nhóm công nghệ chiến lược.',
      tips: [
        'Quỹ nhà nước thường có quy trình và yêu cầu hồ sơ khác quỹ tư nhân. Nên '
        + 'liên hệ Sở Khoa học và Công nghệ để hỏi quy trình hiện hành.',
      ],
    },

    provenance: {
      sources: [
        { url: 'https://dost.hochiminhcity.gov.vn/', fetchedAt: '2026-09-11', note: 'Sở Khoa học và Công nghệ TP.HCM' },
      ],
      lastVerifiedAt: '2026-09-11',
    },
  },

  {
    fundId: 'smedf',
    name: 'Quỹ Phát triển doanh nghiệp nhỏ và vừa (SMEDF)',
    shortName: 'SMEDF',
    slug: 'smedf',
    type: 'GOV',
    vietnamPresence: 'OFFICE',
    website: 'https://www.smedf.gov.vn',
    publish: { status: 'PUBLISHED' },

    thesis: {
      // Đây là khoản VAY phải hoàn trả, có thẩm định tín dụng. Không phải tài trợ.
      capitalTypes: ['DEBT'],
      // Không phải vòng gọi vốn startup — doanh nghiệp cần có phương án kinh
      // doanh và vốn đối ứng, nên để trống bậc giai đoạn.
      stages: [],
      industries: [],
      geos: ['VN'],
      businessModels: [],
      customerTypes: [],
      instruments: [],
      requiresIncorporation: true,
      hardRequirements: [
        {
          code: 'LA_DNNVV',
          field: 'identity.entityType',
          op: 'in',
          value: ['LLC1', 'LLC2', 'JSC'],
          label: 'Phải là doanh nghiệp nhỏ và vừa đủ điều kiện theo quy định',
        },
      ],
    },

    process: {
      submitUrl: 'https://www.smedf.gov.vn/',
      notes: 'Cho vay trực tiếp hoặc uỷ thác qua ngân hàng đối tác.',
    },

    content: {
      summary:
        'Quỹ nhà nước cho doanh nghiệp nhỏ và vừa vay với lãi suất ưu đãi, tối đa '
        + '80% tổng vốn đầu tư của dự án. Không lấy cổ phần.',
      lookingFor: [
        'Doanh nghiệp nhỏ và vừa có phương án kinh doanh khả thi',
        'Có vốn đối ứng cho phần còn lại của dự án',
      ],
      tips: [
        'Đây là khoản vay phải hoàn trả, có thẩm định tín dụng và thường cần tài '
        + 'sản bảo đảm, khác hẳn tài trợ không hoàn lại.',
        'Lãi suất và danh sách ngân hàng nhận uỷ thác thay đổi theo công bố từng '
        + 'thời kỳ, nên kiểm tra lại trên trang quỹ trước khi lập kế hoạch.',
      ],
    },

    provenance: {
      sources: [
        { url: 'https://www.smedf.gov.vn/', fetchedAt: '2026-09-11', note: 'Trang chính thức của quỹ' },
      ],
      lastVerifiedAt: '2026-09-11',
    },
  },

  {
    fundId: '2080-ventures',
    name: '2080 Ventures',
    shortName: '2080 Ventures',
    slug: '2080-ventures',
    type: 'VC',
    // Trang chính thức nêu Việt Nam trong phạm vi hoạt động. Chưa xác nhận được
    // văn phòng riêng, nên khai ở mức đã đầu tư kèm căn cứ.
    vietnamPresence: 'INVESTED',
    vietnamEvidence:
      'Trang chính thức nêu Việt Nam trong phạm vi đầu tư và vận hành chương '
      + 'trình hỗ trợ cho hệ sinh thái khởi nghiệp Việt Nam.',
    website: 'https://2080.ventures',
    publish: { status: 'PUBLISHED' },

    thesis: {
      capitalTypes: ['EQUITY'],
      stages: ['PRESEED', 'SEED', 'SERIES_A'],
      industries: ['AI', 'FINTECH', 'HEALTHTECH', 'ENERGY', 'EDTECH', 'WEB3'],
      geos: ['VN', 'SEA'],
      businessModels: [],
      customerTypes: [],
      instruments: [],
    },

    process: {
      submitUrl: 'https://2080.ventures/get-in-touch',
      notes: 'Trang liên hệ chính thức.',
    },

    content: {
      summary:
        'Nền tảng kết hợp đầu tư và hỗ trợ hệ sinh thái, hoạt động tại Việt Nam '
        + 'và Đông Nam Á, nhắm vào công nghệ giai đoạn sớm.',
      tips: [
        'Quỹ không công bố quy mô séc trên trang chính thức. Hỏi thẳng trong buổi đầu.',
        'Ngoài đầu tư trực tiếp, đơn vị này còn tham gia tài trợ và đồng hành cùng '
        + 'một số chương trình khởi nghiệp tại Việt Nam, đáng theo dõi cả hai kênh.',
      ],
    },


    // Danh mục lấy từ trang danh mục chính thức của quỹ. Con số là TỔNG VÒNG
    // được công bố, không phải séc riêng của quỹ này — xem chú thích trong
    // chuanHoaDanhMuc() ở src/lib/fundModel.js.
    portfolio: {
      source: 'https://2080.ventures',
      verifiedAt: '2026-09-26',
      note: 'Trang danh mục của quỹ nêu doanh nghiệp ở nhiều nước và thiên về công nghệ sâu; chưa có doanh nghiệp Việt Nam nào được nêu công khai.',
      items: [
        {
          name: 'Cerebras',
          whatTheyDo:
            'Thiết kế chip chuyên dụng cỡ lớn để huấn luyện mô hình AI.',
          industries: ['SEMICONDUCTOR', 'AI'],
          geo: 'GLOBAL',
        },
        {
          name: 'SpaceX',
          whatTheyDo:
            'Chế tạo tên lửa tái sử dụng và vận hành mạng internet vệ tinh.',
          industries: ['MANUFACTURING'],
          geo: 'GLOBAL',
        },
        {
          name: 'Animoca Brands',
          whatTheyDo:
            'Phát triển và đầu tư game blockchain.',
          industries: ['WEB3', 'GAMING'],
          geo: 'APAC',
        },
        {
          name: 'Bamboo',
          whatTheyDo:
            'Ứng dụng cho nhà đầu tư châu Phi mua cổ phiếu niêm yết tại Mỹ.',
          industries: ['FINTECH'],
          geo: 'GLOBAL',
        },
        {
          name: 'Yummy',
          whatTheyDo:
            'Siêu ứng dụng giao đồ ăn và đi chợ hộ tại Venezuela.',
          industries: ['FOODBEV', 'LOGISTICS'],
          geo: 'GLOBAL',
        },
      ],
    },
    provenance: {
      sources: [
        { url: 'https://2080.ventures/', fetchedAt: '2026-09-11', note: 'Trang chủ chính thức' },
        { url: 'https://2080.ventures/get-in-touch', fetchedAt: '2026-09-11', note: 'Kênh liên hệ chính thức' },
      ],
      lastVerifiedAt: '2026-09-11',
    },
  },

  {
    fundId: 'angelcentral-vietnam',
    name: 'AngelCentral Việt Nam',
    shortName: 'AngelCentral',
    slug: 'angelcentral-vietnam',
    type: 'ANGEL_NETWORK',
    vietnamPresence: 'OFFICE',
    website: 'https://www.angelcentral.vn',
    publish: { status: 'PUBLISHED' },

    thesis: {
      capitalTypes: ['EQUITY'],
      stages: ['PRESEED', 'SEED', 'SERIES_A'],
      industries: [],
      geos: ['VN', 'SEA'],
      businessModels: [],
      customerTypes: [],
      instruments: [],
    },

    process: {
      submitUrl: 'https://www.angelcentral.vn/',
      notes: 'Nộp hồ sơ để được sàng lọc và mời pitch trước mạng lưới nhà đầu tư.',
    },

    content: {
      summary:
        'Mạng lưới nhà đầu tư thiên thần hoạt động tại Việt Nam và Đông Nam Á, '
        + 'kết nối startup với các nhà đầu tư cá nhân qua buổi pitch có sàng lọc.',
      tips: [
        'Đây là kênh kết nối, không phải một quỹ ra quyết định tập trung. Quy mô '
        + 'mỗi khoản phụ thuộc từng nhà đầu tư tham gia.',
        'Được mời pitch không đồng nghĩa gọi được vốn. Nhưng đây là một trong ít '
        + 'kênh tiếp cận nhà đầu tư cá nhân có tổ chức tại Việt Nam.',
      ],
    },

    provenance: {
      sources: [
        { url: 'https://www.angelcentral.vn/', fetchedAt: '2026-09-12', note: 'Trang Việt Nam chính thức' },
        { url: 'https://www.angelcentral.co/', fetchedAt: '2026-09-12', note: 'Trang mạng lưới khu vực' },
      ],
      lastVerifiedAt: '2026-09-12',
    },
  },

  {
    fundId: 'jenfi',
    name: 'Jenfi',
    shortName: 'Jenfi',
    slug: 'jenfi',
    type: 'LENDER', // Chỉ cấp vốn vay / hoàn trả, không lấy cổ phần.
    vietnamPresence: 'OFFICE',
    website: 'https://jenfi.com',

    // CHƯA HIỂN THỊ. Trang quy trình và trang hỏi đáp nêu hai mức trần khác nhau
    // (200 nghìn và 500 nghìn USD), và điều kiện pháp nhân áp dụng tại Việt Nam
    // chưa rõ. Đưa ra con số sai ở đây khiến người vay tính nhầm hạn mức.
    publish: { status: 'PUBLISHED' },

    thesis: {
      // Cấp vốn hoàn trả theo tỷ lệ doanh thu — không lấy cổ phần.
      // KHÔNG PHẢI ĐẦU TƯ CỔ PHẦN. Đây là cấp vốn theo doanh thu: doanh nghiệp
      // hoàn trả theo tỷ lệ doanh thu, không bán cổ phần. Gán EQUITY sẽ khiến
      // founder tưởng đây là một lựa chọn gọi vốn cổ phần.
      capitalTypes: ['DEBT'],
      // Không gắn giai đoạn gọi vốn: điều kiện là ĐÃ CÓ DOANH THU, không phải
      // đang ở vòng nào.
      stages: [],
      industries: ['ECOMMERCE', 'SAAS_B2B', 'RETAILTECH'],
      geos: ['VN', 'SEA'],
      businessModels: [],
      customerTypes: [],
      instruments: [],
      // Hạn mức tài trợ tham khảo, phụ thuộc pháp nhân, doanh thu và thẩm định.
      ticket: {
        min: usd(10_000), max: usd(1_000_000),
        basis: 'INITIAL_CHECK', confidence: 'MEDIUM', sourceType: 'PRESS',
        hardFilter: false, verifiedAt: '2026-09-15',
      },
      requiresRevenue: true,
      hardRequirements: [
        {
          code: 'SAU_THANG_DOANH_THU',
          field: 'finance.monthsOfRevenue',
          op: 'gte',
          value: 6,
          label: 'Có ít nhất 6 tháng doanh thu kiểm chứng được',
        },
      ],
    },

    process: {
      submitUrl: 'https://jenfi.com/',
      notes: 'Cần xác minh hạn mức và điều kiện pháp nhân áp dụng tại Việt Nam.',
    },

    content: {
      summary:
        'Đơn vị cấp vốn hoàn trả theo doanh thu cho doanh nghiệp số đã có dòng '
        + 'tiền. Không lấy cổ phần, hoàn trả theo tỷ lệ phần trăm doanh thu hàng tháng.',
      tips: [
        'Đây là vốn phải hoàn trả, không phải tài trợ. Chi phí vốn cần so sánh kỹ '
        + 'với vay ngân hàng trước khi quyết định.',
      ],
    },

    provenance: {
      sources: [
        { url: 'https://jenfi.com/', fetchedAt: '2026-09-12', note: 'Trang chính thức, sản phẩm và quy trình' },
      ],
      lastVerifiedAt: '2026-09-15',
    },
  },
  {
    fundId: 'iterative',
    name: 'Iterative',
    shortName: 'Iterative',
    slug: 'iterative',
    type: 'ACCELERATOR',
    vietnamPresence: 'REGION_ONLY',
    website: 'https://www.iterative.vc',
    publish: { status: 'PUBLISHED' },

    thesis: {
      capitalTypes: ['EQUITY'],
      stages: ['IDEA', 'PROTOTYPE', 'PRESEED', 'SEED'],
      industries: [],
      geos: ['SEA'],
      ticket: { min: usd(150_000), max: usd(500_000) },
      businessModels: [],
      customerTypes: [],
      instruments: [],
      hardRequirements: [
        {
          code: 'TOAN_THOI_GIAN',
          field: 'team.founders',
          op: 'someTrue',
          field2: 'fullTime',
          label: 'Người sáng lập phải làm toàn thời gian cho doanh nghiệp',
        },
      ],
    },

    process: { submitUrl: 'https://www.iterative.vc/apply' },

    content: {
      summary:
        'Chương trình tăng tốc tại Đông Nam Á, đầu tư 150 nghìn tới 500 nghìn USD qua SAFE post-money đổi khoảng 10 tới 15 phần trăm cổ phần.',
      tips: [
        'Bắt buộc tham gia chương trình và người sáng lập phải làm toàn thời gian.',
        'Doanh nghiệp phải đặt tại Đông Nam Á hoặc có phần lớn khách hàng trong khu vực.',
      ],
    },

    provenance: {
      sources: [
        { url: 'https://www.iterative.vc/frequently-asked-questions', fetchedAt: '2026-09-11', note: 'Trang hỏi đáp, quy mô đầu tư và điều kiện' },
        { url: 'https://www.iterative.vc/', fetchedAt: '2026-09-11', note: 'Trang chủ chính thức' },
      ],
      lastVerifiedAt: '2026-09-11',
    },
  },

  {
    fundId: 'cocoon-capital',
    name: 'Cocoon Capital',
    shortName: 'Cocoon',
    slug: 'cocoon-capital',
    type: 'VC',
    vietnamPresence: 'REGION_ONLY',
    website: 'https://cocooncap.com',
    publish: { status: 'PUBLISHED' },

    thesis: {
      capitalTypes: ['EQUITY'],
      stages: ['PRESEED', 'SEED'],
      industries: ['SAAS_B2B'],
      geos: ['SEA'],
      ticket: { min: usd(500_000), max: usd(1_500_000) },
      businessModels: [],
      customerTypes: [],
      instruments: [],
    },

    process: { submitUrl: 'https://cocooncap.com/' },

    content: {
      summary:
        'Quỹ đầu tư vòng pre-seed và seed tại Đông Nam Á, tập trung công nghệ doanh nghiệp và công nghệ lõi. Séc từ 500 nghìn tới 1,5 triệu USD.',
      tips: [
        'Quỹ công bố luôn đóng vai trò nhà đầu tư dẫn dắt. Nếu bạn đã có người dẫn khác thì có thể không phù hợp.',
      ],
    },


    // Danh mục lấy từ trang danh mục chính thức của quỹ. Con số là TỔNG VÒNG
    // được công bố, không phải séc riêng của quỹ này — xem chú thích trong
    // chuanHoaDanhMuc() ở src/lib/fundModel.js.
    portfolio: {
      source: 'https://cocooncap.com/portfolio/',
      verifiedAt: '2026-09-26',
      note: 'Danh mục nghiêng hẳn về phần mềm doanh nghiệp và công nghệ sâu ở Singapore; chỉ hai doanh nghiệp Việt Nam được nêu. Mô tả lấy theo đúng cách quỹ ghi trên trang.',
      items: [
        {
          name: 'BuyMed',
          whatTheyDo:
            'Sàn phân phối dược phẩm trực tuyến.',
          industries: ['HEALTHTECH', 'ECOMMERCE'],
          geo: 'VN',
        },
        {
          name: 'Hiip',
          whatTheyDo:
            'Công nghệ tiếp thị qua người ảnh hưởng và thương mại điện tử.',
          industries: ['MARTECH'],
          geo: 'VN',
        },
        {
          name: 'Augmentus',
          whatTheyDo:
            'Lập trình robot công nghiệp không cần viết mã.',
          industries: ['ROBOTICS'],
          geo: 'SEA',
        },
        {
          name: 'Adravision',
          whatTheyDo:
            'Phần mềm quản lý phòng khám nha khoa.',
          industries: ['HEALTHTECH', 'SAAS_B2B'],
          geo: 'SEA',
        },
        {
          name: 'SensorFlow',
          whatTheyDo:
            'Quản lý tiêu thụ năng lượng cho khách sạn.',
          industries: ['ENERGY', 'SAAS_B2B'],
          geo: 'SEA',
        },
        {
          name: 'Qashier',
          whatTheyDo:
            'Máy bán hàng và phần mềm vận hành cho hộ kinh doanh nhỏ.',
          industries: ['RETAILTECH', 'FINTECH'],
          geo: 'SEA',
        },
        {
          name: 'Lendela',
          whatTheyDo:
            'So sánh và kết nối khoản vay cá nhân một cách minh bạch.',
          industries: ['FINTECH'],
          geo: 'SEA',
        },
        {
          name: 'TruTrip',
          whatTheyDo:
            'Quản lý công tác phí và đặt vé cho doanh nghiệp châu Á.',
          industries: ['SAAS_B2B', 'TRAVEL'],
          geo: 'SEA',
        },
      ],
    },
    provenance: {
      sources: [
        { url: 'https://cocooncap.com/', fetchedAt: '2026-09-11', note: 'Trang chủ chính thức, phạm vi đầu tư và quy mô séc' },
      ],
      lastVerifiedAt: '2026-09-11',
    },
  },

  {
    fundId: 'golden-gate-ventures',
    name: 'Golden Gate Ventures',
    shortName: 'Golden Gate',
    slug: 'golden-gate-ventures',
    type: 'VC',
    vietnamPresence: 'REGION_ONLY',
    website: 'https://www.goldengate.vc',
    publish: { status: 'PUBLISHED' },

    thesis: {
      capitalTypes: ['EQUITY'],
      stages: [],
      industries: [],
      geos: ['SEA'],
      businessModels: [],
      customerTypes: [],
      instruments: [],
    },

    process: { submitUrl: null, submitEmail: 'hello@goldengate.vc' },

    content: {
      summary:
        'Quỹ đầu tư giai đoạn sớm tại Đông Nam Á. Trang chính thức không tách rõ từng bậc vòng gọi vốn cũng như quy mô séc.',
      tips: [
        'Quỹ không công bố giai đoạn cụ thể lẫn quy mô séc. Hỏi thẳng trong buổi đầu.',
      ],
    },


    // Danh mục lấy từ trang danh mục chính thức của quỹ. Con số là TỔNG VÒNG
    // được công bố, không phải séc riêng của quỹ này — xem chú thích trong
    // chuanHoaDanhMuc() ở src/lib/fundModel.js.
    portfolio: {
      source: 'https://www.goldengate.vc/portfolio',
      verifiedAt: '2026-09-24',
      note: 'Danh mục của quỹ trải khắp Đông Nam Á; ở đây chỉ liệt kê những doanh nghiệp quỹ ghi rõ có hoạt động tại Việt Nam.',
      items: [
        {
          name: 'Loship',
          whatTheyDo:
            'Giao đồ ăn và hàng hoá nội thành trong vòng một giờ.',
          industries: ['LOGISTICS', 'ECOMMERCE'],
          geo: 'VN',
        },
        {
          name: 'Chợ Tốt',
          whatTheyDo:
            'Sàn rao vặt mua bán đồ đã qua sử dụng.',
          industries: ['ECOMMERCE'],
          geo: 'VN',
        },
        {
          name: 'Appota',
          whatTheyDo:
            'Phát hành game di động và thanh toán số cho người chơi.',
          industries: ['GAMING', 'FINTECH'],
          geo: 'VN',
        },
        {
          name: 'Tititada',
          whatTheyDo:
            'Ứng dụng đầu tư chứng khoán cho nhà đầu tư cá nhân mới bắt đầu.',
          industries: ['FINTECH'],
          geo: 'VN',
        },
        {
          name: 'Mio',
          whatTheyDo:
            'Mạng lưới bán hàng theo nhóm ở khu vực ngoài đô thị lớn.',
          industries: ['ECOMMERCE'],
          geo: 'VN',
        },
        {
          name: 'Bảo Kim',
          whatTheyDo:
            'Cổng thanh toán trực tuyến và dịch vụ tài chính cho doanh nghiệp.',
          industries: ['FINTECH'],
          geo: 'VN',
        },
        {
          name: 'Vui',
          whatTheyDo:
            'Ứng dụng cho người lao động nhận phần lương đã làm trước kỳ trả lương.',
          industries: ['FINTECH'],
          geo: 'VN',
        },
        {
          name: 'Ninja Van',
          whatTheyDo:
            'Giao hàng chặng cuối cho thương mại điện tử khắp Đông Nam Á.',
          industries: ['LOGISTICS'],
          geo: 'SEA',
        },
      ],
    },
    provenance: {
      sources: [
        { url: 'https://www.goldengate.vc/', fetchedAt: '2026-09-11', note: 'Trang chủ chính thức' },
      ],
      lastVerifiedAt: '2026-09-11',
    },
  },

  {
    fundId: 'vertex-ventures-seai',
    name: 'Vertex Ventures Đông Nam Á và Ấn Độ',
    shortName: 'Vertex Ventures',
    slug: 'vertex-ventures-seai',
    type: 'VC',
    vietnamPresence: 'REGION_ONLY',
    website: 'https://www.vertexventures.sg',
    publish: { status: 'PUBLISHED' },

    thesis: {
      capitalTypes: ['EQUITY'],
      stages: ['SEED', 'SERIES_A', 'LATER'],
      industries: [],
      geos: ['SEA'],
      businessModels: [],
      customerTypes: [],
      instruments: [],
    },

    process: { submitUrl: 'https://www.vertexventures.sg/' },

    content: {
      summary:
        'Quỹ đầu tư đa giai đoạn tại Đông Nam Á và Ấn Độ, công bố tham gia từ vòng seed trở đi.',
      tips: [
        'Quỹ tự mô tả không giới hạn ngành. Không nên suy ngành từ danh mục đã đầu tư.',
      ],
    },

    provenance: {
      sources: [
        { url: 'https://www.vertexventures.sg/', fetchedAt: '2026-09-11', note: 'Trang chủ chính thức, phạm vi và giai đoạn đầu tư' },
      ],
      lastVerifiedAt: '2026-09-11',
    },
  },

  {
    fundId: 'beenext',
    name: 'BEENEXT',
    shortName: 'BEENEXT',
    slug: 'beenext',
    type: 'VC',
    vietnamPresence: 'INVESTED',
    vietnamEvidence:
      'Đã đầu tư vào doanh nghiệp Việt Nam; quỹ nêu Việt Nam trong phạm vi hoạt động.',
    website: 'https://www.beenext.com',
    publish: { status: 'PUBLISHED' },

    thesis: {
      capitalTypes: ['EQUITY'],
      stages: ['PRESEED', 'SEED', 'SERIES_A'],
      industries: [],
      geos: ['VN', 'SEA', 'APAC'],
      businessModels: [],
      customerTypes: [],
      instruments: [],
    },

    process: { submitUrl: 'https://www.beenext.com/contact/' },

    content: {
      summary:
        'Quỹ đầu tư giai đoạn sớm hoạt động tại châu Á, đã có thương vụ tại Việt Nam.',
      tips: [
        'Quỹ tự mô tả không giới hạn ngành. Việc đã đầu tư tại Việt Nam không đồng nghĩa có văn phòng riêng ở đây.',
      ],
    },

    provenance: {
      sources: [
        { url: 'https://www.beenext.com/', fetchedAt: '2026-09-11', note: 'Trang chủ chính thức' },
        { url: 'https://www.beenext.com/contact/', fetchedAt: '2026-09-11', note: 'Kênh liên hệ chính thức' },
      ],
      lastVerifiedAt: '2026-09-11',
    },
  },

  {
    fundId: 'cyberagent-capital',
    name: 'CyberAgent Capital',
    shortName: 'CyberAgent',
    slug: 'cyberagent-capital',
    type: 'CVC',
    vietnamPresence: 'INVESTED',
    vietnamEvidence:
      'Danh mục đầu tư công bố trên trang chính thức có doanh nghiệp Việt Nam.',
    website: 'https://www.cyberagentcapital.com',
    publish: { status: 'PUBLISHED' },

    thesis: {
      capitalTypes: ['EQUITY'],
      stages: ['SEED', 'SERIES_A'],
      industries: ['ECOMMERCE', 'FINTECH', 'EDTECH', 'GAMING'],
      geos: ['VN', 'APAC'],
      businessModels: [],
      customerTypes: [],
      instruments: [],
    },

    process: { submitUrl: 'https://www.cyberagentcapital.com/' },

    content: {
      summary:
        'Quỹ đầu tư của tập đoàn CyberAgent, tập trung vòng seed và giai đoạn sớm cho doanh nghiệp số tại châu Á, có danh mục tại Việt Nam.',
      tips: [
        'Quỹ nêu nguyên tắc thường tham gia với vai trò nhà đầu tư đầu tiên.',
      ],
    },

    provenance: {
      sources: [
        { url: 'https://www.cyberagentcapital.com/', fetchedAt: '2026-09-11', note: 'Trang chủ chính thức' },
        { url: 'https://www.cyberagentcapital.com/portfolio/', fetchedAt: '2026-09-11', note: 'Danh mục đầu tư có doanh nghiệp Việt Nam' },
      ],
      lastVerifiedAt: '2026-09-11',
    },
  },

  {
    fundId: 'patamar-capital',
    name: 'Patamar Capital',
    shortName: 'Patamar',
    slug: 'patamar-capital',
    type: 'IMPACT_VC',
    vietnamPresence: 'INVESTED',
    vietnamEvidence:
      'Quỹ nêu Việt Nam trong phạm vi hoạt động và có thương vụ tại đây.',
    website: 'https://patamar.com',
    publish: { status: 'PUBLISHED' },

    thesis: {
      capitalTypes: ['EQUITY'],
      stages: ['SEED', 'SERIES_A', 'LATER'],
      industries: ['FINTECH', 'CLIMATE', 'HEALTHTECH', 'EDTECH', 'AGRITECH', 'SAAS_B2B'],
      geos: ['VN', 'SEA'],
      businessModels: [],
      customerTypes: [],
      instruments: [],
    },

    process: { submitUrl: 'https://patamar.com/contact/' },

    content: {
      summary:
        'Quỹ đầu tư tác động tại Đông Nam Á, nhắm vào mô hình phục vụ nhóm dân cư ít được tiếp cận dịch vụ và các giải pháp khí hậu.',
      tips: [
        'Mô hình phải chứng minh được tác động đo lường được, không chỉ tiềm năng tài chính.',
        'Quỹ dùng nhiều hình thức gồm cổ phần và cho vay, hỏi rõ hình thức áp dụng cho trường hợp của bạn.',
      ],
    },


    // Danh mục lấy từ trang danh mục chính thức của quỹ. Con số là TỔNG VÒNG
    // được công bố, không phải séc riêng của quỹ này — xem chú thích trong
    // chuanHoaDanhMuc() ở src/lib/fundModel.js.
    portfolio: {
      source: 'https://patamar.com/portfolio/',
      verifiedAt: '2026-09-26',
      note: 'Quỹ đầu tư theo hướng tài chính bao trùm và sinh kế cho nhóm thu nhập thấp ở các thị trường mới nổi. Mô tả lấy theo đúng cách quỹ ghi trên trang.',
      items: [
        {
          name: 'Vigo',
          whatTheyDo:
            'Phân phối hàng tiêu dùng B2B không cần kho, dựa trên bán hàng qua mạng xã hội.',
          industries: ['ECOMMERCE', 'LOGISTICS'],
          geo: 'VN',
        },
        {
          name: 'Saladin',
          whatTheyDo:
            'Giải pháp mua bảo hiểm và bồi thường trực tuyến.',
          industries: ['INSURTECH', 'FINTECH'],
          geo: 'VN',
        },
        {
          name: 'SwipeRX',
          whatTheyDo:
            'Kết nối dược sĩ và nhà thuốc độc lập trong khu vực.',
          industries: ['HEALTHTECH', 'SAAS_B2B'],
          geo: 'SEA',
        },
        {
          name: 'Kinara Capital',
          whatTheyDo:
            'Cho vay vốn lưu động cho doanh nghiệp nhỏ và vừa tại Ấn Độ.',
          industries: ['FINTECH'],
          geo: 'APAC',
        },
        {
          name: 'Mapan',
          whatTheyDo:
            'Phân phối hàng hoá tới người tiêu dùng thu nhập thấp qua nền tảng công nghệ.',
          industries: ['ECOMMERCE'],
          geo: 'SEA',
        },
        {
          name: 'Bababos',
          whatTheyDo:
            'Chuỗi cung ứng vật liệu xây dựng B2B kèm điều khoản trả chậm.',
          industries: ['ECOMMERCE', 'FINTECH'],
          geo: 'SEA',
        },
        {
          name: 'FishLog',
          whatTheyDo:
            'Tinh gọn chuỗi cung ứng thuỷ sản tại Indonesia.',
          industries: ['AGRITECH', 'LOGISTICS'],
          geo: 'SEA',
        },
        {
          name: 'Ayoconnect',
          whatTheyDo:
            'Hạ tầng kết nối nền tảng công nghệ với nhà cung cấp dịch vụ thanh toán hoá đơn.',
          industries: ['FINTECH'],
          geo: 'SEA',
        },
      ],
    },
    provenance: {
      sources: [
        { url: 'https://patamar.com/', fetchedAt: '2026-09-11', note: 'Trang chủ chính thức, luận điểm đầu tư' },
        { url: 'https://patamar.com/contact/', fetchedAt: '2026-09-11', note: 'Kênh liên hệ chính thức' },
      ],
      lastVerifiedAt: '2026-09-11',
    },
  },

  {
    // Nền tảng có nhiều quỹ thành viên với khẩu vị khác nhau. Gộp làm một bản ghi
    // sẽ cho ra giai đoạn và quy mô séc sai với tất cả. Cần tách trước khi hiển thị.
    fundId: 'quest-ventures',
    name: 'Quest Ventures',
    shortName: 'Quest',
    slug: 'quest-ventures',
    type: 'VC',
    vietnamPresence: 'REGION_ONLY',
    website: 'https://www.questventures.com',
    publish: { status: 'PUBLISHED' },

    thesis: {
      capitalTypes: ['EQUITY'],
      stages: ['PRESEED', 'SEED', 'SERIES_A'],
      industries: ['AI', 'ECOMMERCE', 'FINTECH', 'FOODBEV', 'INSURTECH', 'LOGISTICS', 'MEDIA', 'PROPTECH', 'GAMING'],
      geos: ['VN', 'SEA'],
      businessModels: [],
      customerTypes: [],
      instruments: [],
      // Khoảng 250 nghìn – 2 triệu USD chỉ xuất hiện ở nguồn tổng hợp bên thứ
      // ba, quỹ chưa xác nhận. Hiển thị để tham khảo, không được loại cứng.
      ticket: {
        min: usd(250_000), max: usd(2_000_000),
        basis: 'INITIAL_CHECK', confidence: 'LOW', sourceType: 'THIRD_PARTY_ESTIMATE',
        hardFilter: false, verifiedAt: '2026-09-15',
      },
    },

    process: { submitUrl: 'https://www.questventures.com/contact/' },

    content: {
      summary:
        'Nền tảng đầu tư tại châu Á vận hành nhiều quỹ và chương trình khác nhau.',
    },

    provenance: {
      sources: [
        { url: 'https://www.questventures.com/', fetchedAt: '2026-09-11', note: 'Trang chủ chính thức' },
      ],
      lastVerifiedAt: '2026-09-15',
    },
  },

  {
    // Trang chính thức hiện có rất ít thông tin về khẩu vị đầu tư. Không đủ căn cứ
    // để điền giai đoạn, ngành hay quy mô séc.
    fundId: 'monks-hill-ventures',
    name: 'Monk\u2019s Hill Ventures',
    shortName: 'Monk\u2019s Hill',
    slug: 'monks-hill-ventures',
    type: 'VC',
    vietnamPresence: 'REGION_ONLY',
    website: 'https://www.monkshill.com',
    publish: { status: 'PUBLISHED' },

    thesis: {
      capitalTypes: ['EQUITY'],
      stages: ['SERIES_A', 'LATER'],
      industries: [],
      geos: ['SEA'],
      businessModels: [],
      customerTypes: [],
      instruments: [],
    },

    process: { submitUrl: 'https://www.monkshill.com/' },

    content: {
      summary:
        'Quỹ đầu tư công nghệ tại Đông Nam Á.',
    },


    // Danh mục lấy từ trang danh mục chính thức của quỹ. Con số là TỔNG VÒNG
    // được công bố, không phải séc riêng của quỹ này — xem chú thích trong
    // chuanHoaDanhMuc() ở src/lib/fundModel.js.
    portfolio: {
      source: 'https://www.monkshill.com/portfolio',
      verifiedAt: '2026-09-24',
      note: 'Quỹ không công bố số tiền của từng thương vụ.',
      items: [
        {
          name: '1Long',
          whatTheyDo:
            'Ứng dụng tích luỹ và đầu tư số tiền nhỏ cho người mới bắt đầu.',
          industries: ['FINTECH'],
          geo: 'VN',
        },
        {
          name: 'Saladin',
          whatTheyDo:
            'Nền tảng so sánh và phân phối bảo hiểm trực tuyến.',
          industries: ['INSURTECH', 'FINTECH'],
          geo: 'VN',
        },
        {
          name: 'Vucar',
          whatTheyDo:
            'Thu mua và bán lại ô tô cũ, kiểm định trước khi bán.',
          industries: ['ECOMMERCE', 'MOBILITY'],
          geo: 'VN',
        },
        {
          name: 'INKR',
          whatTheyDo:
            'Nền tảng đọc và dịch truyện tranh có bản quyền.',
          industries: ['MEDIA'],
          geo: 'VN',
        },
        {
          name: 'ELSA',
          whatTheyDo:
            'Ứng dụng luyện phát âm tiếng Anh bằng nhận dạng giọng nói.',
          industries: ['EDTECH', 'AI'],
          geo: 'GLOBAL',
        },
        {
          name: 'Glints',
          whatTheyDo:
            'Nền tảng tuyển dụng và quản lý nhân sự cho doanh nghiệp Đông Nam Á.',
          industries: ['HRTECH'],
          geo: 'SEA',
        },
        {
          name: 'Jenfi',
          whatTheyDo:
            'Cấp vốn hoàn trả theo doanh thu cho doanh nghiệp bán hàng trực tuyến.',
          industries: ['FINTECH'],
          geo: 'SEA',
        },
        {
          name: 'Ninja Van',
          whatTheyDo:
            'Giao hàng chặng cuối cho thương mại điện tử khắp Đông Nam Á.',
          industries: ['LOGISTICS'],
          geo: 'SEA',
        },
      ],
    },
    provenance: {
      sources: [
        { url: 'https://www.monkshill.com/', fetchedAt: '2026-09-11', note: 'Trang chủ chính thức' },
      ],
      lastVerifiedAt: '2026-09-15',
    },
  },

  {
    // Trang chính thức dựng bằng JavaScript nên chưa lấy được thông tin khẩu vị và
    // kênh nộp hồ sơ một cách ổn định.
    fundId: 'insignia-ventures-partners',
    name: 'Insignia Ventures Partners',
    shortName: 'Insignia',
    slug: 'insignia-ventures-partners',
    type: 'VC',
    vietnamPresence: 'REGION_ONLY',
    website: 'https://www.insignia.vc',
    publish: { status: 'PUBLISHED' },

    thesis: {
      capitalTypes: ['EQUITY'],
      stages: ['SEED', 'SERIES_A', 'LATER'],
      industries: [],
      geos: ['SEA'],
      businessModels: [],
      customerTypes: [],
      instruments: [],
      // Khoảng 100 nghìn – 5 triệu USD từ nguồn tổng hợp, chưa phải mandate
      // quỹ công bố. Tham khảo, không loại cứng.
      ticket: {
        min: usd(100_000), max: usd(5_000_000),
        basis: 'INITIAL_CHECK', confidence: 'LOW', sourceType: 'THIRD_PARTY_ESTIMATE',
        hardFilter: false, verifiedAt: '2026-09-15',
      },
    },

    process: { submitUrl: 'https://www.insignia.vc/' },

    content: {
      summary:
        'Quỹ đầu tư công nghệ tại Đông Nam Á.',
    },

    provenance: {
      sources: [
        { url: 'https://www.insignia.vc/', fetchedAt: '2026-09-11', note: 'Trang chủ chính thức' },
      ],
      lastVerifiedAt: '2026-09-15',
    },
  },

  {
    // CHỈ CÓ NGUỒN BÁO CHÍ. Trang chính thức không truy cập ổn định, nên toàn bộ
    // thông tin giai đoạn và ngành đang dựa trên bài báo năm 2023. Theo quy tắc dữ
    // liệu, phải có quỹ xác nhận trước khi hiển thị.
    fundId: 'febe-ventures',
    name: 'FEBE Ventures',
    shortName: 'FEBE',
    slug: 'febe-ventures',
    type: 'VC',
    vietnamPresence: 'INVESTED',
    vietnamEvidence:
      'Quỹ nêu Việt Nam trong phạm vi hoạt động hoặc có thương vụ tại Việt Nam.',
    website: 'https://febe.vc',
    publish: { status: 'PUBLISHED' },

    thesis: {
      capitalTypes: ['EQUITY'],
      stages: ['PRESEED', 'SEED'],
      industries: ['SAAS_B2B', 'ECOMMERCE', 'AI', 'HEALTHTECH', 'CLIMATE'],
      geos: ['VN', 'SEA'],
      businessModels: [],
      customerTypes: [],
      instruments: [],
      // Séc điển hình theo báo chí: 250 nghìn USD ở pre-seed, khoảng 750 nghìn
      // USD ở seed. Quỹ chưa tự công bố nên không dùng làm tiêu chí loại cứng.
      ticket: {
        min: usd(250_000), max: usd(750_000),
        basis: 'INITIAL_CHECK', confidence: 'MEDIUM', sourceType: 'PRESS',
        hardFilter: false, verifiedAt: '2026-09-15',
      },
    },

    process: { submitUrl: 'https://febe.vc/' },

    content: {
      summary:
        'Quỹ đầu tư giai đoạn sớm tập trung vào doanh nghiệp gắn với Việt Nam.',
    },

    provenance: {
      sources: [
        { url: 'https://theinvestor.vn/vietnam-focused-febe-ventures-announces-75-mln-second-fund-d7796.html', fetchedAt: '2026-09-11', note: 'Bài báo về quỹ thứ hai' },
        { url: 'https://vietcetera.com/en/febe-ventures-initiates-75m-fund-ii-to-empower-global-startups', fetchedAt: '2026-09-11', note: 'Bài báo về quỹ thứ hai' },
      ],
      lastVerifiedAt: '2026-09-15',
    },
  },

  {
    // KHÔNG PHẢI QUỸ CHO STARTUP GIAI ĐOẠN SỚM. Các thương vụ gần đây ở mức 8,8 tới
    // 25 triệu USD. Cần bổ sung tiêu chí quy mô doanh thu trước khi hiển thị, nếu
    // không founder pre-seed sẽ thấy quỹ này trong kết quả.
    fundId: 'mekong-capital',
    name: 'Mekong Capital',
    shortName: 'Mekong Capital',
    slug: 'mekong-capital',
    type: 'PE_GROWTH',
    vietnamPresence: 'OFFICE',
    website: 'https://www.mekongcapital.com',
    publish: { status: 'PUBLISHED' },

    thesis: {
      capitalTypes: ['EQUITY'],
      stages: ['LATER'],
      industries: ['RETAILTECH', 'FOODBEV', 'HEALTHTECH', 'EDTECH', 'BIOTECH', 'AGRITECH'],
      geos: ['VN'],
      businessModels: [],
      customerTypes: [],
      instruments: [],
      // KHÔNG PHẢI MANDATE. Quỹ không công bố khoảng séc hiện hành; đây là
      // khoảng của hai thương vụ đã công bố gần đây (HUSK 5 triệu USD, F88
      // 20 triệu USD). Tiêu chí thật sự quan trọng với Mekong là doanh nghiệp
      // đã qua giai đoạn tăng trưởng, kinh tế đơn vị đã chứng minh, và quỹ
      // thường nắm thiểu số khoảng 30–49%.
      ticket: {
        min: usd(5_000_000), max: usd(20_000_000),
        basis: 'OBSERVED_DEALS', confidence: 'LOW', sourceType: 'PRESS',
        hardFilter: false, verifiedAt: '2026-09-15',
      },
    },

    process: { submitUrl: 'https://www.mekongcapital.com/contact-us/' },

    content: {
      summary:
        'Quỹ đầu tư tăng trưởng tại Việt Nam, mua cổ phần thiểu số lớn ở doanh nghiệp đã có quy mô.',
    },


    // Danh mục lấy từ trang danh mục chính thức của quỹ. Con số là TỔNG VÒNG
    // được công bố, không phải séc riêng của quỹ này — xem chú thích trong
    // chuanHoaDanhMuc() ở src/lib/fundModel.js.
    portfolio: {
      source: 'https://www.mekongcapital.com/our-investments/',
      verifiedAt: '2026-09-25',
      note: 'Quỹ không công bố số tiền của từng khoản đầu tư.',
      items: [
        {
          name: 'Thế Giới Di Động',
          whatTheyDo:
            'Chuỗi bán lẻ điện thoại và điện máy.',
          industries: ['RETAILTECH'],
          geo: 'VN',
          roundDate: '2007-05',
          outcome: 'EXITED',
          outcomeNote: 'Đầu tư 5/2007, thoái vốn 1/2018.',
        },
        {
          name: 'Golden Gate',
          whatTheyDo:
            'Chuỗi nhà hàng lẩu nướng (Kichi-Kichi, Gogi House, Ashima).',
          industries: ['FOODBEV'],
          geo: 'VN',
          roundDate: '2008-04',
          outcome: 'EXITED',
          outcomeNote: 'Đầu tư 4/2008, bán lại cổ phần 8/2014.',
        },
        {
          name: 'Pizza 4P\'s',
          whatTheyDo:
            'Chuỗi nhà hàng pizza tự làm phô mai tại Việt Nam.',
          industries: ['FOODBEV'],
          geo: 'VN',
          outcome: 'EXITED',
          outcomeNote: 'Thoái vốn năm 2022.',
        },
        {
          name: 'F88',
          whatTheyDo:
            'Chuỗi cửa hàng cho vay có tài sản bảo đảm.',
          industries: ['FINTECH'],
          geo: 'VN',
        },
        {
          name: 'Gene Solutions',
          whatTheyDo:
            'Xét nghiệm gen phát hiện sớm bệnh lý.',
          industries: ['HEALTHTECH', 'BIOTECH'],
          geo: 'VN',
        },
        {
          name: 'Pharmacity',
          whatTheyDo:
            'Chuỗi nhà thuốc bán lẻ.',
          industries: ['HEALTHTECH', 'RETAILTECH'],
          geo: 'VN',
        },
        {
          name: 'Marou',
          whatTheyDo:
            'Sô cô la thủ công làm từ hạt ca cao trồng trong nước.',
          industries: ['FOODBEV'],
          geo: 'VN',
        },
        {
          name: 'Vua Nệm',
          whatTheyDo:
            'Chuỗi bán lẻ nệm và đồ ngủ.',
          industries: ['RETAILTECH'],
          geo: 'VN',
        },
      ],
    },
    provenance: {
      sources: [
        { url: 'https://www.mekongcapital.com/', fetchedAt: '2026-09-11', note: 'Trang chủ chính thức' },
      ],
      lastVerifiedAt: '2026-09-15',
    },
  },

  {
    // Đã đầu tư tại Việt Nam nhưng ở vòng rất lớn. Không nên khớp với startup giai
    // đoạn sớm chỉ vì có thương vụ Việt Nam trong danh mục.
    fundId: 'b-capital-growth',
    name: 'B Capital, Growth',
    shortName: 'B Capital',
    slug: 'b-capital-growth',
    type: 'VC',
    vietnamPresence: 'INVESTED',
    vietnamEvidence:
      'Quỹ nêu Việt Nam trong phạm vi hoạt động hoặc có thương vụ tại Việt Nam.',
    website: 'https://b.capital',
    publish: { status: 'PUBLISHED' },

    thesis: {
      capitalTypes: ['EQUITY'],
      stages: ['LATER'],
      industries: ['SAAS_B2B', 'AI', 'CYBERSECURITY', 'FINTECH', 'HEALTHTECH', 'LOGISTICS'],
      geos: ['VN', 'APAC', 'GLOBAL'],
      businessModels: [],
      customerTypes: [],
      instruments: [],
      // Chiến lược growth quỹ đã công bố: 10–60 triệu USD, vòng Series B–D.
      ticket: {
        min: usd(10_000_000), max: usd(60_000_000),
        basis: 'INITIAL_CHECK', confidence: 'HIGH', sourceType: 'OFFICIAL',
        hardFilter: true, verifiedAt: '2026-09-15',
      },
    },

    process: { submitUrl: 'https://b.capital/contact/' },

    content: {
      summary:
        'Nền tảng đầu tư đa giai đoạn toàn cầu, đã tham gia vòng lớn tại Việt Nam.',
    },

    provenance: {
      sources: [
        { url: 'https://b.capital/', fetchedAt: '2026-09-11', note: 'Trang chủ chính thức' },
      ],
      lastVerifiedAt: '2026-09-15',
    },
  },

  {
    // TÁCH RIÊNG KHỎI B CAPITAL GROWTH — hai chiến lược, hai mandate.
    //
    // Gộp chung một hồ sơ thì engine lấy khoảng séc 10–60 triệu USD của quỹ
    // growth áp cho founder pre-seed, và loại họ ra khỏi một quỹ thực ra đang
    // có vehicle dành cho đúng giai đoạn của họ.
    fundId: 'b-capital-early',
    name: 'B Capital, Early Stage',
    shortName: 'B Capital Early',
    slug: 'b-capital-early',
    type: 'VC',
    vietnamPresence: 'INVESTED',
    vietnamEvidence:
      'Quỹ nêu Việt Nam trong phạm vi hoạt động hoặc có thương vụ tại Việt Nam.',
    website: 'https://b.capital',
    publish: { status: 'PUBLISHED' },

    thesis: {
      capitalTypes: ['EQUITY'],
      stages: ['SEED', 'SERIES_A'],
      industries: ['AI', 'SAAS_B2B', 'HEALTHTECH', 'CLIMATE'],
      geos: ['VN', 'APAC', 'GLOBAL'],
      businessModels: [],
      customerTypes: [],
      instruments: [],
      // Quỹ early-stage mới lập năm 2026; mức giải ngân dự kiến theo báo chí
      // uy tín, quỹ chưa tự công bố mandate nên không dùng để loại cứng.
      ticket: {
        min: usd(500_000), max: usd(10_000_000),
        basis: 'INITIAL_CHECK', confidence: 'MEDIUM', sourceType: 'PRESS',
        hardFilter: false, verifiedAt: '2026-09-15',
      },
    },

    process: {
      submitUrl: 'https://b.capital/contact/',
      notes: 'Liên hệ qua biểu mẫu trên website.',
    },

    content: {
      summary:
        'Nhánh đầu tư giai đoạn sớm của B Capital, lập năm 2026, đầu tư vào công nghệ, y tế và khí hậu.',
    },

    provenance: {
      sources: [
        { url: 'https://b.capital/', fetchedAt: '2026-09-15', note: 'Trang chủ chính thức' },
      ],
      lastVerifiedAt: '2026-09-15',
    },
  },

  {
    // Nền tảng gồm cả quỹ giai đoạn sớm và mảng cho vay tăng trưởng. Cần tách hai
    // mảng, nếu không sẽ gán nhầm hình thức cho vay cho startup giai đoạn sớm.
    fundId: 'january-capital',
    name: 'January Capital',
    shortName: 'January Capital',
    slug: 'january-capital',
    type: 'VC',
    vietnamPresence: 'INVESTED',
    vietnamEvidence:
      'Quỹ nêu Việt Nam trong phạm vi hoạt động hoặc có thương vụ tại Việt Nam.',
    website: 'https://www.january.capital',
    publish: { status: 'PUBLISHED' },

    thesis: {
      capitalTypes: ['EQUITY'],
      stages: ['PRESEED', 'SEED'],
      // "commerce enablement, fintech infrastructure, health/energy/agriculture,
      // AI & SaaS" — quỹ công bố nhóm quan tâm chứ không giới hạn cứng.
      industries: ['ECOMMERCE', 'FINTECH', 'HEALTHTECH', 'ENERGY', 'AGRITECH', 'AI', 'SAAS_B2B'],
      geos: ['VN', 'APAC'],
      businessModels: [],
      customerTypes: [],
      instruments: [],
      // Quỹ công bố khoản đầu tư ban đầu 0,5–2 triệu USD, có thể giải ngân
      // tiếp tới 25 triệu USD. Dùng khoản BAN ĐẦU vì đó mới là mức quyết định
      // một vòng gọi vốn có phù hợp hay không.
      ticket: {
        min: usd(500_000), max: usd(2_000_000),
        basis: 'INITIAL_CHECK', confidence: 'HIGH', sourceType: 'OFFICIAL',
        hardFilter: true, verifiedAt: '2026-09-15',
      },
    },

    process: { submitUrl: 'https://www.january.capital/contact' },

    content: {
      summary:
        'Quỹ đầu tư công nghệ tại châu Á Thái Bình Dương, hướng tới là nhà đầu tư thể chế đầu tiên của doanh nghiệp.',
    },


    // Danh mục lấy từ trang danh mục chính thức của quỹ. Con số là TỔNG VÒNG
    // được công bố, không phải séc riêng của quỹ này — xem chú thích trong
    // chuanHoaDanhMuc() ở src/lib/fundModel.js.
    portfolio: {
      source: 'https://www.january.capital/portfolio',
      verifiedAt: '2026-09-26',
      note: 'Mô tả lấy theo đúng cách quỹ ghi trên trang danh mục.',
      items: [
        {
          name: 'Aemi',
          whatTheyDo:
            'Công cụ vận hành và chuỗi cung ứng số cho ngành chăm sóc sức khoẻ, làm đẹp.',
          industries: ['ECOMMERCE', 'SAAS_B2B'],
          stage: 'SEED',
          geo: 'VN',
        },
        {
          name: 'Inflow',
          whatTheyDo:
            'Chuỗi cung ứng thời trang với sản xuất theo yêu cầu.',
          industries: ['MANUFACTURING', 'ECOMMERCE'],
          stage: 'SEED',
          geo: 'VN',
        },
        {
          name: 'Qode',
          whatTheyDo:
            'Tinh gọn tuyển dụng cho các tổ chức thiên về công nghệ.',
          industries: ['HRTECH', 'AI'],
          stage: 'SEED',
          geo: 'VN',
        },
        {
          name: 'CoLearn',
          whatTheyDo:
            'Học trực tuyến cho học sinh phổ thông tại Indonesia.',
          industries: ['EDTECH'],
          stage: 'SEED',
          geo: 'SEA',
        },
        {
          name: 'Akulaku',
          whatTheyDo:
            'Cho vay tiêu dùng và mua trước trả sau tại Indonesia.',
          industries: ['FINTECH'],
          geo: 'SEA',
        },
        {
          name: 'aCommerce',
          whatTheyDo:
            'Vận hành thương mại điện tử thuê ngoài cho các thương hiệu.',
          industries: ['ECOMMERCE', 'LOGISTICS'],
          geo: 'SEA',
        },
        {
          name: 'Omni HR',
          whatTheyDo:
            'Phần mềm quản trị nhân sự cho doanh nghiệp Đông Nam Á.',
          industries: ['HRTECH', 'SAAS_B2B'],
          geo: 'SEA',
        },
        {
          name: 'Go1',
          whatTheyDo:
            'Kho khoá học trực tuyến cho doanh nghiệp đào tạo nhân viên.',
          industries: ['EDTECH', 'SAAS_B2B'],
          geo: 'GLOBAL',
        },
      ],
    },
    provenance: {
      sources: [
        { url: 'https://www.january.capital/', fetchedAt: '2026-09-11', note: 'Trang chủ chính thức' },
        { url: 'https://www.january.capital/portfolio', fetchedAt: '2026-09-11', note: 'Danh mục đầu tư' },
        { url: 'https://www.january.capital/', fetchedAt: '2026-09-15', note: 'Quỹ công bố khoản đầu tư ban đầu 0,5–2 triệu USD; hơn 75% thương vụ ở vòng vốn tổ chức đầu tiên' },
      ],
      lastVerifiedAt: '2026-09-15',
    },
  },

  {
    // Cần xác minh lại các thương vụ Việt Nam trong danh mục và tình trạng nhận hồ
    // sơ hiện tại trước khi hiển thị.
    fundId: 'kk-fund',
    name: 'KK Fund',
    shortName: 'KK Fund',
    slug: 'kk-fund',
    type: 'VC',
    vietnamPresence: 'INVESTED',
    vietnamEvidence:
      'Quỹ nêu Việt Nam trong phạm vi hoạt động hoặc có thương vụ tại Việt Nam.',
    website: 'https://kkfund.co',
    publish: { status: 'PUBLISHED' },

    thesis: {
      capitalTypes: ['EQUITY'],
      stages: ['PRESEED', 'SEED'],
      industries: ['FINTECH', 'EDTECH', 'HRTECH', 'MOBILITY', 'HEALTHTECH', 'PROPTECH', 'WEB3', 'MEDIA'],
      geos: ['VN', 'SEA'],
      businessModels: [],
      customerTypes: [],
      instruments: [],
      // Quỹ công bố séc khoảng 400–500 nghìn USD, nhưng nguồn từ 2016. Dữ liệu
      // cũ gần mười năm thì không đủ để loại một founder ra khỏi kết quả.
      ticket: {
        min: usd(400_000), max: usd(500_000),
        basis: 'INITIAL_CHECK', confidence: 'LOW', sourceType: 'OFFICIAL_STALE',
        hardFilter: false, verifiedAt: '2026-09-15',
      },
    },

    process: {
      submitUrl: null,
      submitEmail: 'info@kkfund.co',
      notes: 'Quỹ nhận hồ sơ qua email; không có biểu mẫu nộp trực tuyến.',
    },

    content: {
      summary:
        'Quỹ đầu tư giai đoạn sớm cho doanh nghiệp công nghệ tại Đông Nam Á và Đông Á.',
    },

    provenance: {
      sources: [
        { url: 'https://kkfund.co/', fetchedAt: '2026-09-11', note: 'Trang chủ chính thức, luận điểm đầu tư' },
        { url: 'https://kkfund.co/portfolio/', fetchedAt: '2026-09-11', note: 'Danh mục đầu tư' },
      ],
      lastVerifiedAt: '2026-09-15',
    },
  },

  {
    // Chưa đủ nguồn hiện hành để xác định quy mô séc, quỹ đang hoạt động và kênh
    // nhận hồ sơ.
    fundId: 'rebright-partners',
    name: 'Rebright Partners',
    shortName: 'Rebright',
    slug: 'rebright-partners',
    type: 'VC',
    vietnamPresence: 'INVESTED',
    vietnamEvidence:
      'Quỹ nêu Việt Nam trong phạm vi hoạt động hoặc có thương vụ tại Việt Nam.',
    website: 'https://rebrightpartners.com',
    publish: { status: 'DRAFT' },

    thesis: {
      capitalTypes: ['EQUITY'],
      stages: ['PRESEED', 'SEED', 'SERIES_A'],
      industries: [],
      geos: ['VN', 'SEA'],
      businessModels: [],
      customerTypes: [],
      instruments: [],
    },

    process: { submitUrl: 'https://rebrightpartners.com/' },

    content: {
      summary:
        'Quỹ đầu tư giai đoạn sớm gốc Nhật Bản, hoạt động tại Đông Nam Á và Ấn Độ.',
    },


    // Danh mục lấy từ trang danh mục chính thức của quỹ. Con số là TỔNG VÒNG
    // được công bố, không phải séc riêng của quỹ này — xem chú thích trong
    // chuanHoaDanhMuc() ở src/lib/fundModel.js.
    portfolio: {
      source: 'https://rebrightpartners.com/portfolio/',
      verifiedAt: '2026-09-27',
      note: 'Danh mục công khai của quỹ tập trung ở Ấn Độ và Indonesia; chưa có doanh nghiệp Việt Nam nào được nêu. Mô tả lấy theo đúng cách quỹ ghi trên trang.',
      items: [
        {
          name: 'Medikabazaar',
          whatTheyDo:
            'Sàn thương mại điện tử B2B vật tư y tế lớn tại Ấn Độ.',
          industries: ['HEALTHTECH', 'ECOMMERCE'],
          geo: 'APAC',
        },
        {
          name: 'LetsTransport',
          whatTheyDo:
            'Sàn thuê xe tải giao hàng chặng cuối trong đô thị.',
          industries: ['LOGISTICS'],
          geo: 'APAC',
        },
        {
          name: 'MediBuddy',
          whatTheyDo:
            'Ứng dụng tư vấn bác sĩ trực tuyến.',
          industries: ['HEALTHTECH'],
          geo: 'APAC',
        },
        {
          name: 'Aquaconnect',
          whatTheyDo:
            'Giải pháp trọn gói cho hộ nuôi tôm và cá.',
          industries: ['AGRITECH'],
          geo: 'APAC',
        },
        {
          name: 'Bukalapak',
          whatTheyDo:
            'Sàn thương mại điện tử lớn tại Indonesia.',
          industries: ['ECOMMERCE'],
          geo: 'SEA',
        },
        {
          name: 'Coins.ph',
          whatTheyDo:
            'Dịch vụ tài chính trên di động và blockchain cho người chưa có tài khoản ngân hàng.',
          industries: ['FINTECH', 'WEB3'],
          geo: 'SEA',
        },
      ],
    },
    provenance: {
      sources: [
        { url: 'https://rebrightpartners.com/', fetchedAt: '2026-09-11', note: 'Trang chủ chính thức' },
        { url: 'https://rebrightpartners.com/portfolio/', fetchedAt: '2026-09-11', note: 'Danh mục đầu tư' },
      ],
      lastVerifiedAt: '2026-09-11',
    },
  },
  {
    fundId: 'vsv-capital',
    name: 'VSV Capital (Vietnam Silicon Valley)',
    shortName: 'VSV Capital',
    slug: 'vsv-capital',
    type: 'ACCELERATOR',
    vietnamPresence: 'OFFICE',
    website: 'https://vsvcapital.com.vn',
    publish: { status: 'PUBLISHED' },

    thesis: {
      capitalTypes: ['EQUITY'],
      stages: ['PRESEED', 'SEED'],
      industries: [],
      geos: ['VN'],
      // "Up to 50.000 USD" đổi 7 đến 10% cổ phần, theo trang Accelerator chính thức.
      ticket: { max: usd(50_000), basis: 'INITIAL_CHECK', confidence: 'HIGH', sourceType: 'OFFICIAL', verifiedAt: '2026-10-07' },
      businessModels: [],
      customerTypes: [],
      instruments: [],
    },

    process: {
      submitUrl: 'https://vsvcapital.com.vn/accelerator',
      notes: 'Nộp hồ sơ trực tuyến qua F6S, phỏng vấn online, rồi trình bày trước hội đồng đầu tư tại Hà Nội. Toàn bộ quy trình bằng tiếng Anh.',
    },

    content: {
      summary:
        'Chương trình tăng tốc khởi nghiệp hoạt động từ năm 2014 tại Hà Nội, dành cho startup '
        + 'giai đoạn pre-seed và seed. Đầu tư tối đa 50 nghìn USD mỗi đội, đổi 7 đến 10% cổ phần, '
        + 'kèm 4 tháng bootcamp, mỗi khoá 10 đến 15 đội.',
      tips: [
        'Tính riêng phần tiền thì định giá ngầm định khá thấp: 50 nghìn USD đổi 10% tương đương '
        + 'định giá sau vòng khoảng 500 nghìn USD. Phần còn lại bạn trả cho huấn luyện và mạng lưới nhà đầu tư.',
        'Trang chính thức không ghi lịch khoá mới. Hỏi trước thời hạn nộp của khoá gần nhất.',
        'Có thêm chương trình Climate Tech Catalyst cho startup nông nghiệp và kinh tế tuần hoàn.',
      ],
    },

    provenance: {
      sources: [
        { url: 'https://vsvcapital.com.vn/', fetchedAt: '2026-10-07', note: 'Trang chính thức' },
        { url: 'https://vsvcapital.com.vn/accelerator', fetchedAt: '2026-10-07', note: 'Điều kiện, quy mô đầu tư và quy trình' },
      ],
      lastVerifiedAt: '2026-10-07',
    },
  },

  {
    fundId: 'vinventures',
    name: 'VinVentures (Vingroup)',
    shortName: 'VinVentures',
    slug: 'vinventures',
    type: 'CVC',
    vietnamPresence: 'OFFICE',
    website: 'https://www.vinventures.net',
    publish: { status: 'PUBLISHED' },

    thesis: {
      capitalTypes: ['EQUITY'],
      // B.O.U.N.D 2026 nhắm vào startup đã có giải pháp chạy được và bằng chứng thương mại hoá ban đầu.
      stages: ['SEED', 'SERIES_A'],
      // Kết nối với các công ty trong tập đoàn (VinFast, VinRobotics, Vinhomes, Vinmec, VinEnergo...).
      industries: ['MOBILITY', 'ROBOTICS', 'PROPTECH', 'HEALTHTECH', 'ENERGY', 'AI'],
      geos: ['VN'],
      // "At least USD 500K investment per startup" cho chương trình B.O.U.N.D 2026.
      ticket: { min: usd(500_000), basis: 'INITIAL_CHECK', confidence: 'HIGH', sourceType: 'OFFICIAL', hardFilter: false, verifiedAt: '2026-10-07' },
      businessModels: [],
      customerTypes: [],
      instruments: [],
    },

    process: {
      submitUrl: 'https://www.vinventures.net/',
      submitEmail: 'contact@vinventures.net',
      notes: 'Chương trình B.O.U.N.D 2026 là cửa vào chính. Liên hệ qua email hoặc trang chính thức để biết thời hạn nộp.',
    },

    content: {
      summary:
        'Quỹ đầu tư của Tập đoàn Vingroup. Chương trình B.O.U.N.D 2026 đầu tư tối thiểu 500 nghìn USD '
        + 'cho mỗi startup được chọn, nhắm vào công ty đã có giải pháp chạy được và tín hiệu thương mại hoá, '
        + 'kết nối với các công ty trong tập đoàn.',
      tips: [
        'Lợi thế lớn nhất thường không phải tiền mà là khách hàng và kênh thử nghiệm trong hệ sinh thái Vingroup. '
        + 'Hãy chỉ ra cụ thể công ty nào trong tập đoàn sẽ dùng sản phẩm của bạn.',
        'Là quỹ của doanh nghiệp, mục tiêu có cả chiến lược. Hỏi rõ về quyền ưu tiên mua lại và ràng buộc độc quyền trước khi ký.',
        'VinVentures cũng phát hành báo cáo thường niên về thị trường đầu tư mạo hiểm Việt Nam, đáng đọc trước khi gặp.',
      ],
    },

    provenance: {
      sources: [
        { url: 'https://www.vinventures.net/', fetchedAt: '2026-10-07', note: 'Trang chính thức, chương trình B.O.U.N.D 2026' },
      ],
      lastVerifiedAt: '2026-10-07',
    },
  },
  {
    fundId: 'genai-fund',
    name: 'GenAI Fund',
    shortName: 'GenAI Fund',
    slug: 'genai-fund',
    type: 'VC',
    vietnamPresence: 'OFFICE',
    website: 'https://genaifund.ai',
    publish: { status: 'PUBLISHED' },

    thesis: {
      capitalTypes: ['EQUITY'],
      // Trang chủ: "invests in AI-first technology companies in Southeast Asia".
      industries: ['AI'],
      // Quỹ chỉ nói "we invest as early as possible", không liệt kê giai đoạn cụ thể.
      // Để trống thay vì tự quy đổi thành pre-seed/seed.
      stages: [],
      geos: ['VN', 'SEA'],
      // Trang chính thức không công bố quy mô quỹ hay mức séc → không có trường ticket.
      // Con số quỹ 10 triệu USD chỉ có trên DealStreetAsia (bài trả phí), không dùng.
      businessModels: [],
      customerTypes: [],
      instruments: [],
    },

    process: {
      submitUrl: 'https://genaifund.ai/#get-in-touch',
      notes: 'Biểu mẫu "Get in touch" cuối trang chủ, quỹ đề nghị ghi kèm link pitch deck nếu đang gọi vốn. '
        + 'Ngoài ra quỹ thường gặp startup qua các chương trình ghép với doanh nghiệp (GenAI Open Innovation, FastTrack AI Accelerator).',
    },

    content: {
      summary:
        'Quỹ đầu tư mạo hiểm tập trung vào AI, đặt tại TP.HCM, đầu tư từ rất sớm vào công ty lấy AI làm lõi '
        + 'ở Đông Nam Á. Quỹ đồng thời vận hành các chương trình ghép startup với doanh nghiệp lớn để chạy thử nghiệm (PoC), '
        + 'nên con đường thường gặp là đi qua một chương trình doanh nghiệp trước khi được xét đầu tư.',
      lookingFor: [
        'Công ty lấy AI làm lõi, bán cho doanh nghiệp (phần lớn danh mục là phần mềm B2B)',
        'Đội có thể chạy thử nghiệm với tập đoàn và chứng minh giá trị bằng số liệu',
      ],
      tips: [
        'Quỹ có hai vai: đầu tư, và bán dịch vụ đổi mới sáng tạo cho doanh nghiệp (hackathon, ghép cặp, PoC, xây dựng startup cùng tập đoàn). '
        + 'Được chọn vào một chương trình doanh nghiệp không có nghĩa là đang được thẩm định đầu tư; hỏi rõ khi nào chuyển sang bàn chuyện vốn.',
        'Trang chính thức không công bố quy mô quỹ hay mức đầu tư mỗi khoản. Hỏi thẳng trong buổi đầu thay vì đoán.',
        'Một PoC có số liệu rõ với doanh nghiệp trong mạng lưới của quỹ là cách nói chuyện đầu tư thuyết phục nhất. '
        + 'Ví dụ: Bonbon thắng chương trình Wash3000 làm cùng Tasco rồi được quỹ cùng rót vòng pre-seed.',
      ],
    },

    // Danh mục lấy từ trang danh mục chính thức (cập nhật 05/12/2025) và bài công bố
    // khoản đầu tư Bonbon (23/09/2026). Trang chính thức không nêu số tiền các vòng
    // khác nên để trống, không lấy số từ PitchBook, Caplight hay Briter.
    portfolio: {
      source: 'https://genaifund.ai/our-portfolio/',
      verifiedAt: '2026-10-13',
      items: [
        {
          name: 'Bonbon',
          url: 'https://bonbon.com.vn',
          whatTheyDo: 'Kết nối chủ xe với gara và dịch vụ chăm sóc xe; đội thắng chương trình Tasco Foundry Wash3000.',
          industries: ['MOBILITY'],
          stage: 'PRESEED',
          geo: 'VN',
          roundDate: '2026-09',
          roundSize: usd(500000),
          roundSource: 'https://genaifund.ai/2026/09/23/genai-fund-backs-bonbon-500k/',
          leadInvestor: false,
        },
        {
          name: 'Arcanic AI',
          url: 'https://arcanic.ai',
          whatTheyDo: 'Giải pháp AI giúp doanh nghiệp và cơ quan nhà nước tự động hoá quy trình.',
          industries: ['AI', 'SAAS_B2B'],
          geo: 'VN',
        },
        {
          name: 'Tribee',
          url: 'https://tribee.io/en/',
          whatTheyDo: 'Nền tảng bán hàng cho đội tuyến đầu (tài chính, kênh truyền thống, bán lẻ, F&B), phân tích hành vi để huấn luyện nhân viên.',
          industries: ['AI', 'SAAS_B2B'],
          geo: 'VN',
        },
        {
          name: 'Presight',
          url: 'https://www.presight.io',
          whatTheyDo: 'Phân tích dữ liệu và tri thức nội bộ của doanh nghiệp, đưa ra gợi ý hành động.',
          industries: ['AI', 'SAAS_B2B'],
          geo: 'VN',
        },
        {
          name: 'Revve AI',
          url: 'https://www.revve.ai',
          whatTheyDo: 'Trợ lý AI bán hàng và chăm sóc khách hàng chạy liên tục qua giọng nói, chat, tin nhắn.',
          industries: ['AI', 'SAAS_B2B'],
          geo: 'VN',
        },
        {
          name: 'LIFE AI',
          url: 'https://www.lifenetwork.ai',
          whatTheyDo: 'Kết hợp phòng thí nghiệm sinh học, trung tâm dữ liệu GPU và mô hình nền riêng để rút ngắn khám phá thuốc, y học chính xác.',
          industries: ['AI', 'HEALTHTECH', 'BIOTECH'],
          geo: 'VN',
        },
        {
          name: 'Blaze AI',
          url: 'https://blaze.vn',
          whatTheyDo: 'Nền tảng AI giọng nói cho Đông Nam Á: chuyển văn bản thành giọng nói, giọng nói thành văn bản, tách người nói.',
          industries: ['AI'],
          geo: 'VN',
        },
        {
          name: 'Shieldbase (Malaysia)',
          url: 'https://shieldbase.ai',
          whatTheyDo: 'Hệ điều hành AI nối dữ liệu rời rạc từ công cụ AI và hệ thống cũ thành quy trình tự động.',
          industries: ['AI', 'SAAS_B2B'],
          geo: 'SEA',
        },
        {
          name: 'Fortii (Malaysia)',
          url: 'https://www.fortii.ai',
          whatTheyDo: 'Quản trị và bảo mật dữ liệu, AI; quyền riêng tư và kiểm soát tuân thủ.',
          industries: ['AI', 'CYBERSECURITY'],
          geo: 'SEA',
        },
        {
          name: 'ourteam (Malaysia)',
          url: 'https://ourteam.ai',
          whatTheyDo: 'AI tuyển dụng, sàng lọc toàn bộ ứng viên cho doanh nghiệp.',
          industries: ['AI', 'HRTECH'],
          geo: 'SEA',
        },
        {
          name: 'Superagent (Thái Lan)',
          url: 'http://www.superagent.co',
          whatTheyDo: 'Môi giới bất động sản vận hành bằng AI cho chủ đầu tư và chủ nhà.',
          industries: ['AI', 'PROPTECH'],
          geo: 'SEA',
        },
      ],
    },

    provenance: {
      sources: [
        { url: 'https://genaifund.ai/', fetchedAt: '2026-10-13', note: 'Trang chính thức: luận điểm đầu tư, kênh liên hệ, chương trình' },
        { url: 'https://genaifund.ai/our-portfolio/', fetchedAt: '2026-10-13', note: 'Danh mục đầu tư' },
        { url: 'https://genaifund.ai/2026/09/23/genai-fund-backs-bonbon-500k/', fetchedAt: '2026-10-13', note: 'Khoản đầu tư Bonbon, vòng pre-seed 500.000 USD do Tasco dẫn dắt' },
        { url: 'https://beyondpilot.ai/programs/nestle-vietnam-ai-reinvention', fetchedAt: '2026-10-13', note: 'Chương trình Nestlé Vietnam AI Reinvention do quỹ vận hành' },
      ],
      lastVerifiedAt: '2026-10-13',
    },
  },
  {
    fundId: 'vic-partners',
    name: 'VIC Partners',
    shortName: 'VIC Partners',
    slug: 'vic-partners',
    type: 'VC',
    vietnamPresence: 'OFFICE',
    website: 'https://vic.partners',
    publish: { status: 'PUBLISHED' },

    thesis: {
      capitalTypes: ['EQUITY'],
      // Trang Giới thiệu không giới hạn ngành; chỉ nói startup có mô hình kinh doanh
      // đổi mới dựa trên công nghệ và dữ liệu.
      industries: [],
      // "Seed to Pre-Series A funding gap" theo trang Giới thiệu chính thức.
      stages: ['SEED', 'SEED_PLUS'],
      geos: ['VN'],
      // Không công bố quy mô séc → không có trường ticket.
      businessModels: [],
      customerTypes: [],
      instruments: [],
    },

    process: {
      submitUrl: 'https://vic.partners/form/',
      notes: 'Biểu mẫu ngắn trên trang chính thức: tên, nơi đặt trụ sở (Hà Nội hoặc TP.HCM), ngành, thị trường, '
        + 'một câu mô tả startup; có thể kèm số liệu lực kéo và link pitch deck hoặc dự phóng tài chính.',
    },

    content: {
      summary:
        'Nhóm đầu tư do các nhà sáng lập, nhà đầu tư chuyên nghiệp và lãnh đạo doanh nghiệp lập ra (hơn 20 partner), '
        + 'có quỹ I từ 2016 đến 2017 và quỹ II từ 06/2021. Đầu tư vào startup có gắn với Việt Nam ở khoảng trống '
        + 'từ seed tới trước Series A, kèm hỗ trợ gọi vốn, tài chính, vận hành và tuyển dụng.',
      lookingFor: [
        'Startup có mô hình kinh doanh đổi mới dựa trên công nghệ và dữ liệu, gắn với thị trường Việt Nam',
        'Đội đã qua vòng đầu và cần vốn để đi tới Series A',
      ],
      tips: [
        'Giá trị lớn nhất là mạng lưới partner. Trong buổi đầu, hỏi cụ thể partner nào hiểu ngành của bạn và có thể giới thiệu khách hàng nào.',
        'Trang chính thức không công bố mức séc. Hỏi thẳng thay vì đoán.',
        'Biểu mẫu chỉ cần một câu mô tả, nên câu đó phải nói được khách hàng là ai, vấn đề gì và lực kéo hiện tại.',
      ],
    },

    portfolio: {
      source: 'https://vic.partners/investment-strategies/',
      verifiedAt: '2026-10-14',
      items: [
        {
          name: 'TopCV',
          url: 'https://www.topcv.vn',
          whatTheyDo: 'Nền tảng tuyển dụng và công nghệ nhân sự, kết nối ứng viên với nhà tuyển dụng.',
          industries: ['HRTECH'],
          geo: 'VN',
        },
      ],
    },

    provenance: {
      sources: [
        { url: 'https://vic.partners/about/', fetchedAt: '2026-10-14', note: 'Giới thiệu: giai đoạn seed tới pre-Series A, quỹ I và quỹ II, tập trung Việt Nam' },
        { url: 'https://vic.partners/investment-strategies/', fetchedAt: '2026-10-14', note: 'Chiến lược đầu tư, ví dụ TopCV' },
        { url: 'https://vic.partners/form/', fetchedAt: '2026-10-14', note: 'Biểu mẫu nhận hồ sơ' },
      ],
      lastVerifiedAt: '2026-10-14',
    },
  },

  {
    fundId: 'tin-men-capital',
    name: 'Tin Men Capital',
    shortName: 'Tin Men Capital',
    slug: 'tin-men-capital',
    type: 'VC',
    vietnamPresence: 'REGION_ONLY',
    website: 'https://www.tinmen.asia',
    publish: { status: 'PUBLISHED' },

    thesis: {
      capitalTypes: ['EQUITY'],
      // "We invest in enterprise technology and B2B marketplace startups": sản xuất, logistics,
      // xây dựng, hàng hải, nông nghiệp, bất động sản.
      industries: ['SAAS_B2B', 'MANUFACTURING', 'LOGISTICS', 'AGRITECH', 'PROPTECH'],
      // Chỉ đầu tư khi đã có doanh thu đáng kể; lời kêu gọi trên trang chủ nhắm Series A.
      // Không tự quy đổi thành danh sách giai đoạn cụ thể.
      stages: [],
      geos: ['SEA'],
      // "A few hundred thousand dollars" tới vài triệu USD: không đủ chính xác để ghi số.
      businessModels: [],
      customerTypes: ['B2B'],
      instruments: [],
    },

    process: {
      submitUrl: 'https://www.tinmen.asia/contact/founders',
      submitEmail: 'startups@tinmen.asia',
      notes: 'Biểu mẫu "Pitch to us" trên trang chính thức; nếu biểu mẫu lỗi, gửi email startups@tinmen.asia.',
    },

    content: {
      summary:
        'Quỹ đầu tư mạo hiểm tại Singapore, chỉ đầu tư vào công ty công nghệ B2B ở Đông Nam Á: phần mềm doanh nghiệp '
        + 'và sàn B2B cho các ngành truyền thống như sản xuất, logistics, xây dựng, hàng hải, nông nghiệp, bất động sản. '
        + 'Chỉ đầu tư vào công ty đã có doanh thu đáng kể, và luôn dẫn dắt vòng khi rót séc đầu tiên.',
      lookingFor: [
        'Công ty B2B đã có doanh thu đáng kể, không nhận giai đoạn ý tưởng',
        'Phần mềm doanh nghiệp hoặc sàn B2B cho ngành truyền thống ở Đông Nam Á',
      ],
      tips: [
        'Séc đầu tiên từ vài trăm nghìn USD cho công ty giai đoạn sớm tới vài triệu USD cho công ty đi xa hơn, theo trang nhận hồ sơ chính thức.',
        'Quỹ chỉ đầu tư khi được dẫn dắt vòng. Nếu vòng của bạn đã có nhà đầu tư dẫn dắt, nhiều khả năng không phù hợp.',
        'Công ty đặt trụ sở ngoài Đông Nam Á được yêu cầu chuyển trụ sở về khu vực, thường là Singapore. '
        + 'Startup Việt Nam nên hỏi sớm liệu có phải dựng pháp nhân Singapore không.',
        'Chưa thấy công ty Việt Nam trong danh mục công khai. Đây là quỹ khu vực, hãy hỏi kinh nghiệm của họ với thị trường Việt Nam.',
      ],
    },

    provenance: {
      sources: [
        { url: 'https://www.tinmen.asia/', fetchedAt: '2026-10-14', note: 'Trang chính thức: chỉ đầu tư B2B tại Đông Nam Á' },
        { url: 'https://www.tinmen.asia/contact/founders', fetchedAt: '2026-10-14', note: 'Tiêu chí, mức séc đầu tiên, cách nộp hồ sơ' },
        { url: 'https://www.tinmen.asia/portfolio', fetchedAt: '2026-10-14', note: 'Danh mục (các công ty trang 1 đều đã thoái vốn)' },
      ],
      lastVerifiedAt: '2026-10-14',
    },
  },
  /* ─── Bổ sung 19/10/2026: đợt 1 sàng lọc 18 quỹ (xem ghi chú Project sang-loc-18-quy-2026-10) ───
   * Cả năm đơn vị đều có tên trong danh sách "2024 most active investors" của
   * Vietnam Innovation & Private Capital Report 2025 (NIC, VPCA, BCG, Do Ventures).
   */
  {
    fundId: 'aiviet-venture',
    name: 'AiViet Venture',
    shortName: 'AiViet',
    slug: 'aiviet-venture',
    type: 'VC',
    vietnamPresence: 'OFFICE',
    vietnamEvidence: 'Văn phòng tại Bitexco Nam Long, 63A Võ Văn Tần, TP.HCM (trang Liên hệ chính thức).',
    website: 'https://aiviet.vn',
    publish: { status: 'PUBLISHED' },

    thesis: {
      capitalTypes: ['EQUITY'],
      // Trang chủ: "empowering Vietnamese entrepreneurs who operate or intend to operate
      // scalable startups". Không giới hạn ngành, không nêu giai đoạn cụ thể ("from
      // ideation to high-growth stage") nên để trống thay vì tự quy đổi.
      industries: [],
      stages: [],
      geos: ['VN'],
      // Không công bố mức séc.
      businessModels: [],
      customerTypes: [],
      instruments: [],
    },

    process: {
      submitEmail: 'contact@aiviet.vn',
      ctaType: 'contact',
      notes: 'Trang Liên hệ có nút "Send your pitch to us" mở email contact@aiviet.vn; không nêu mẫu hồ sơ bắt buộc.',
    },

    content: {
      summary:
        'Đơn vị xây dựng và đầu tư startup (venture builder) tại TP.HCM, thành lập năm 2023, hỗ trợ founder Việt '
        + 'từ giai đoạn ý tưởng tới tăng trưởng nhanh. Hội đồng gồm bà Đinh Thị Hoa (Phó Chủ tịch Chứng khoán Thiên Việt), '
        + 'ông Nguyễn Mạnh Tường (MoMo), ông Lưu Vĩnh Lợi (Kyber Network) và ông Nguyễn Linh (Athena Studio). '
        + 'Danh mục công bố có hơn 25 công ty Việt Nam.',
      lookingFor: [
        'Founder người Việt đang làm hoặc chuẩn bị làm một mô hình kinh doanh có khả năng mở rộng lớn',
        'Startup có thể tạo thay đổi rõ rệt trong ngành của mình',
      ],
      tips: [
        'Danh mục trải rộng nhiều ngành (xe điện, nông nghiệp, y tế, bảo hiểm, tài chính, bất động sản), nên hãy nói rõ vì sao mô hình của bạn mở rộng được, thay vì chứng minh ngành của bạn hợp với quỹ.',
        'Danh mục trùng nhiều với Chứng khoán Thiên Việt (Dat Bike, Citics, NamiTech, Khaicode). Nếu đã gửi một bên, hỏi xem hai bên có xem hồ sơ chung không trước khi gửi bên kia.',
        'Trang chính thức không công bố mức séc và giai đoạn. Hỏi thẳng ở buổi gặp đầu.',
      ],
    },

    portfolio: {
      source: 'https://aiviet.vn/portfolio',
      verifiedAt: '2026-10-19',
      note: 'Trang danh mục liệt kê 27 công ty, không kèm mô tả. Mô tả dưới đây lấy từ trang chính thức của từng công ty.',
      items: [
        { name: 'Dat Bike', url: 'https://dat.bike', whatTheyDo: 'Thiết kế và bán xe máy điện.', industries: ['MOBILITY'], geo: 'VN' },
        { name: 'BonbonCar', url: 'https://www.bonboncar.vn', whatTheyDo: 'Ứng dụng thuê xe ô tô tự lái theo gói giờ ở TP.HCM, Hà Nội, Đà Nẵng.', industries: ['MOBILITY'], geo: 'VN' },
        { name: 'NamiTech', url: 'https://www.namitech.io', whatTheyDo: 'Công nghệ AI về giọng nói: xác thực bằng giọng nói, phân tích hội thoại, lọc tạp âm cuộc gọi.', industries: ['AI'], geo: 'VN' },
        { name: 'Genetica', url: 'https://genetica.asia/en', whatTheyDo: 'Xét nghiệm gen cho người châu Á để cá nhân hoá dinh dưỡng, vận động và phát hiện sớm rủi ro sức khoẻ.', industries: ['HEALTHTECH', 'BIOTECH'], geo: 'VN' },
        { name: 'Enfarm', url: 'https://enfarm.com', whatTheyDo: 'Cảm biến dinh dưỡng đất theo thời gian thực kèm tư vấn canh tác bằng AI.', industries: ['AGRITECH'], geo: 'VN' },
        { name: 'Citics', url: 'https://citics.vn', whatTheyDo: 'Nền tảng dữ liệu và AI định giá bất động sản cho ngân hàng, môi giới và đơn vị thẩm định.', industries: ['PROPTECH', 'AI'], geo: 'VN' },
        { name: 'Tingee', url: 'https://tingee.vn', whatTheyDo: 'API và thiết bị báo nhận thanh toán không tiền mặt, tự động đối soát cho cửa hàng.', industries: ['FINTECH'], geo: 'VN' },
        { name: 'BAO Insurtech', url: 'https://baoinsure.ai', whatTheyDo: 'Bảo hiểm ô tô cá nhân hoá bằng dữ liệu, nền tảng bán bảo hiểm cho đại lý.', industries: ['INSURTECH'], geo: 'VN' },
        { name: 'Révi', url: 'https://revi.vn', whatTheyDo: 'Chuỗi cà phê và trà ứng dụng công nghệ, do cựu lãnh đạo Gojek lập.', industries: ['FOODBEV'], geo: 'VN', roundDate: '2024-02' },
      ],
    },

    provenance: {
      sources: [
        { url: 'https://aiviet.vn', fetchedAt: '2026-10-19', note: 'Giới thiệu, luận điểm đầu tư, hội đồng' },
        { url: 'https://aiviet.vn/contact/', fetchedAt: '2026-10-19', note: 'Địa chỉ TP.HCM, email nhận hồ sơ' },
        { url: 'https://aiviet.vn/portfolio', fetchedAt: '2026-10-19', note: 'Danh mục 27 công ty' },
        { url: 'https://www.dealstreetasia.com/?p=381404', fetchedAt: '2026-10-19', note: 'Vòng của Révi (02/2024) có AiViet Venture tham gia' },
        { url: 'https://web-assets.bcg.com/69/30/a6baab2b47b8a58e7181953ae024/vietnam-innovation-private-capital-report-2025.pdf', fetchedAt: '2026-10-19', note: 'Danh sách nhà đầu tư hoạt động mạnh tại Việt Nam năm 2024' },
      ],
      lastVerifiedAt: '2026-10-19',
    },
  },

  {
    fundId: 'tvs-chung-khoan-thien-viet',
    name: 'Chứng khoán Thiên Việt (TVS)',
    shortName: 'TVS',
    slug: 'tvs-chung-khoan-thien-viet',
    type: 'CVC',
    vietnamPresence: 'OFFICE',
    vietnamEvidence: 'Trụ sở tại Hà Nội và TP.HCM (trang chính thức tvs.vn).',
    website: 'https://www.tvs.vn',
    publish: { status: 'PUBLISHED' },

    thesis: {
      capitalTypes: ['EQUITY'],
      // Trang "Đầu tư vốn" nêu bốn mảng, có "Đầu tư vốn mạo hiểm", nhưng không nêu ngành,
      // giai đoạn hay mức séc. Ba ngành fintech, edtech, healthtech là phát biểu của tổng
      // giám đốc trên báo (VnEconomy 12/2021), không phải trang chính thức, nên không điền.
      industries: [],
      stages: [],
      geos: ['VN'],
      businessModels: [],
      customerTypes: [],
      instruments: [],
    },

    process: {
      submitEmail: 'info@tvs.vn',
      ctaType: 'contact',
      notes: 'Không có kênh nhận hồ sơ startup riêng. Email chung info@tvs.vn; văn phòng TP.HCM ở Bitexco Nam Long, 63A Võ Văn Tần.',
    },

    content: {
      summary:
        'Công ty chứng khoán có mảng đầu tư vốn tư nhân và vốn mạo hiểm bằng tiền của chính công ty. '
        + 'Là cổ đông lớn của MoMo từ giai đoạn đầu, nhà đầu tư thiên thần của Finhay, và dẫn dắt vòng pre-Series A '
        + '2 triệu USD của NamiTech (10/2023). Có tên trong danh sách nhà đầu tư hoạt động mạnh tại Việt Nam năm 2024.',
      lookingFor: [
        'Founder có tham vọng ở những khoảng trống thị trường TVS tìm ra bằng nghiên cứu từ dưới lên (trang Đầu tư vốn)',
        'Theo phát biểu của lãnh đạo TVS trên báo năm 2021: ưu tiên fintech, edtech và healthtech',
      ],
      tips: [
        'TVS đầu tư bằng bảng cân đối của công ty chứng khoán, không phải quỹ có thời hạn. Hỏi rõ kỳ vọng thời gian nắm giữ và cách thoái vốn.',
        'Danh mục trùng nhiều với AiViet Venture, nơi Phó Chủ tịch TVS ngồi hội đồng. Một lời giới thiệu qua mạng lưới này thường hiệu quả hơn email chung.',
        'TVS có hệ sinh thái riêng (Galaxy, MoMo, MindX). Nếu sản phẩm của bạn hợp tác được với các công ty này, nói rõ trong lần tiếp cận đầu.',
      ],
    },

    portfolio: {
      source: 'https://www.tvs.vn/vi/tu-doanh',
      verifiedAt: '2026-10-19',
      note: 'Trang "Thương vụ nổi bật" chỉ có logo, gồm cả doanh nghiệp lớn như TPBank, 24h, Pizza 4P\'s. Dưới đây chỉ giữ các công ty công nghệ khởi nghiệp.',
      items: [
        { name: 'MoMo', url: 'https://momo.vn', whatTheyDo: 'Siêu ứng dụng ví điện tử và thanh toán.', industries: ['FINTECH'], geo: 'VN' },
        { name: 'Finhay', whatTheyDo: 'Ứng dụng đầu tư; TVS đầu tư từ vòng thiên thần.', industries: ['FINTECH'], geo: 'VN', stage: 'ANGEL' },
        {
          name: 'NamiTech', url: 'https://www.namitech.io', whatTheyDo: 'Công nghệ AI về giọng nói: xác thực bằng giọng nói, phân tích hội thoại, lọc tạp âm cuộc gọi.',
          industries: ['AI'], geo: 'VN', stage: 'SEED_PLUS', roundDate: '2023-10', leadInvestor: true,
          roundSize: usd(2_000_000), roundSource: 'https://theleader.vn/chung-khoan-thien-viet-rot-von-vao-linh-vuc-ai-d6840.html',
        },
        { name: 'Dat Bike', url: 'https://dat.bike', whatTheyDo: 'Thiết kế và bán xe máy điện.', industries: ['MOBILITY'], geo: 'VN' },
        { name: 'Citics', url: 'https://citics.vn', whatTheyDo: 'Nền tảng dữ liệu và AI định giá bất động sản cho ngân hàng, môi giới và đơn vị thẩm định.', industries: ['PROPTECH', 'AI'], geo: 'VN' },
        { name: 'MindX', whatTheyDo: 'Trường đào tạo công nghệ.', industries: ['EDTECH'], geo: 'VN' },
      ],
    },

    provenance: {
      sources: [
        { url: 'https://www.tvs.vn/vi/tu-doanh', fetchedAt: '2026-10-19', note: 'Mảng đầu tư vốn: vốn tư nhân, vốn mạo hiểm; logo thương vụ nổi bật' },
        { url: 'https://www.tvs.vn', fetchedAt: '2026-10-19', note: 'Địa chỉ, email liên hệ' },
        { url: 'https://theleader.vn/chung-khoan-thien-viet-rot-von-vao-linh-vuc-ai-d6840.html', fetchedAt: '2026-10-19', note: 'Dẫn dắt vòng pre-Series A 2 triệu USD của NamiTech (24/10/2023)' },
        { url: 'https://vneconomy.vn/sau-momo-tvs-va-galaxy-entertainment-education-tiep-tuc-nham-den-ky-lan-thu-2.htm', fetchedAt: '2026-10-19', note: 'Cổ đông MoMo, thiên thần Finhay; ba ngành ưu tiên (12/2021)' },
        { url: 'https://web-assets.bcg.com/69/30/a6baab2b47b8a58e7181953ae024/vietnam-innovation-private-capital-report-2025.pdf', fetchedAt: '2026-10-19', note: 'Danh sách nhà đầu tư hoạt động mạnh tại Việt Nam năm 2024' },
      ],
      lastVerifiedAt: '2026-10-19',
    },
  },

  {
    fundId: 'theventures',
    name: 'TheVentures',
    shortName: 'TheVentures',
    slug: 'theventures',
    type: 'VC',
    vietnamPresence: 'OFFICE',
    vietnamEvidence: 'Chi nhánh Việt Nam từ 2015; quỹ riêng cho Việt Nam khoảng 15 tỷ won do partner đặt tại TP.HCM quản lý (Money Today, 06/2023).',
    website: 'https://theventures.vc',
    publish: { status: 'PUBLISHED' },

    thesis: {
      capitalTypes: ['EQUITY'],
      // Trang chủ: "early-stage investor", đầu tư cả khi chưa có lực kéo hoặc đang làm MVP.
      // Không liệt kê giai đoạn cụ thể nên để trống.
      industries: [],
      stages: [],
      geos: ['VN', 'APAC'],
      // Mức 5 đến 10 tỷ won cho quỹ Việt Nam là con số trên báo, không phải trang chính thức.
      businessModels: [],
      customerTypes: [],
      instruments: [],
    },

    process: {
      submitUrl: 'https://theventures.vc/apply',
      submitEmail: 'info@theventures.vc',
      ctaType: 'apply',
      notes: 'Nhận hồ sơ quanh năm qua trang Apply, phản hồi trong ngày làm việc kế tiếp. Vòng phỏng vấn trực tiếp ở văn phòng Seoul.',
    },

    content: {
      summary:
        'Quỹ đầu tư giai đoạn sớm của Hàn Quốc, lập năm 2014, đã đầu tư hơn 260 công ty. Có chi nhánh Việt Nam từ 2015 '
        + 'và từ 2023 có quỹ riêng cho Việt Nam khoảng 15 tỷ won, đầu tư từ giai đoạn trước Series A với mức 5 đến 10 tỷ won '
        + 'mỗi công ty (theo báo Hàn Quốc). Năm 2023 đầu tư 4 startup Việt Nam.',
      lookingFor: [
        'Đội ngũ và thị trường tốt, kể cả khi chưa có lực kéo hoặc mới đang làm MVP',
        'Theo phát biểu năm 2023 về quỹ Việt Nam: thương mại điện tử, nhất là trang bán hàng chuyên ngành kết hợp nội dung',
      ],
      tips: [
        'Trang Apply viết cho startup Hàn Quốc và hẹn phỏng vấn ở Seoul. Founder Việt nên ghi rõ đang gọi vốn cho quỹ Việt Nam để hồ sơ được chuyển tới đội ở TP.HCM.',
        'Quỹ nhấn mạnh tốc độ: phản hồi sau một ngày làm việc và giải ngân trong khoảng một tuần. Chuẩn bị sẵn data room trước khi nộp.',
        'Danh mục Việt Nam nghiêng về tiêu dùng và B2B có dữ liệu (Citics, Filum.ai). Nêu rõ số liệu khách hàng thật ngay trong hồ sơ.',
      ],
    },

    portfolio: {
      source: 'https://theventures.vc/companies',
      verifiedAt: '2026-10-19',
      note: 'Trang danh mục ghi địa điểm Việt Nam cho 4 công ty, chỉ kèm lĩnh vực. Hai công ty chưa đủ căn cứ mô tả (BARUN VINA, TUBUDD) không đưa vào.',
      items: [
        { name: 'Citics', url: 'https://citics.vn', whatTheyDo: 'Nền tảng dữ liệu và AI định giá bất động sản cho ngân hàng, môi giới và đơn vị thẩm định.', industries: ['PROPTECH', 'AI'], geo: 'VN' },
        { name: 'Filum.ai', url: 'https://filum.ai', whatTheyDo: 'Gom tin nhắn Zalo, Messenger, Shopee, email về một hộp thư, có AI trả lời bán hàng và chăm sóc khách.', industries: ['SAAS_B2B', 'AI'], geo: 'VN' },
      ],
    },

    provenance: {
      sources: [
        { url: 'https://theventures.vc', fetchedAt: '2026-10-19', note: 'Giai đoạn sớm, cách nộp hồ sơ, số công ty đã đầu tư' },
        { url: 'https://theventures.vc/apply', fetchedAt: '2026-10-19', note: 'Quy trình nộp và phản hồi' },
        { url: 'https://theventures.vc/companies', fetchedAt: '2026-10-19', note: 'Danh mục, có ghi địa điểm' },
        { url: 'https://www.mt.co.kr/future/2023/06/21/2023061209215493770', fetchedAt: '2026-10-19', note: 'Quỹ Việt Nam 15 tỷ won, séc 5 đến 10 tỷ won, ưu tiên thương mại điện tử (21/06/2023)' },
        { url: 'https://en.wowtale.net/2024/01/30/70948', fetchedAt: '2026-10-19', note: '4 startup Việt Nam trong năm 2023' },
      ],
      lastVerifiedAt: '2026-10-19',
    },
  },

  {
    fundId: 'mynavi',
    name: 'Mynavi',
    shortName: 'Mynavi',
    slug: 'mynavi',
    type: 'CVC',
    vietnamPresence: 'OFFICE',
    vietnamEvidence: 'Mynavi Vietnam Co., Ltd. thành lập 07/05/2024 tại TP.HCM để tìm và hỗ trợ khoản đầu tư tại Việt Nam.',
    website: 'https://www.mynavi.jp/global-strategy/',
    publish: { status: 'PUBLISHED' },

    thesis: {
      capitalTypes: ['EQUITY'],
      // Trang Global Strategy: "Tech × Education, HR, and BPO Businesses". Không nêu giai đoạn.
      industries: ['HRTECH', 'EDTECH'],
      stages: [],
      geos: ['VN', 'APAC'],
      businessModels: [],
      customerTypes: [],
      instruments: [],
    },

    process: {
      submitUrl: 'https://www.mynavi.jp/global-strategy/',
      ctaType: 'contact',
      notes: 'Không có quy trình nhận hồ sơ công bố; trang Global Strategy có biểu mẫu "Contact us".',
    },

    content: {
      summary:
        'Tập đoàn nhân sự và truyền thông Nhật Bản, đầu tư chiến lược vào startup công nghệ trong mảng nhân sự, giáo dục và dịch vụ thuê ngoài. '
        + 'Tại Việt Nam đã đầu tư TopCV, ITviec, 1Office, MindX, WeCare 247, và lập Mynavi Vietnam ở TP.HCM năm 2024 '
        + 'để tìm thêm khoản đầu tư.',
      lookingFor: [
        'Startup công nghệ trong tuyển dụng, quản trị nhân sự, giáo dục và dịch vụ thuê ngoài',
        'Doanh nghiệp có thể dùng kinh nghiệm vận hành dịch vụ nhân sự của Mynavi tại Nhật',
      ],
      tips: [
        'Đây là nhà đầu tư chiến lược, không phải quỹ tài chính. Lợi ích chính là kinh nghiệm và quan hệ của Mynavi; đổi lại, hỏi rõ quyền đi kèm như ghế hội đồng hay quyền ưu tiên mua.',
        'Mynavi thường rót thêm ở vòng sau và có lúc nâng lên thành công ty liên kết (như TopCV năm 2023). Tính trước điều này ảnh hưởng thế nào tới các nhà đầu tư khác và tới phương án thoái vốn.',
        'Ngoài nhân sự và giáo dục thì gần như không hợp. Đừng gửi nếu sản phẩm không chạm tới người lao động hoặc người học.',
      ],
    },

    portfolio: {
      source: 'https://www.mynavi.jp/global-strategy/',
      verifiedAt: '2026-10-19',
      items: [
        { name: 'TopCV', url: 'https://www.topcv.vn', whatTheyDo: 'Nền tảng tạo CV và kết nối việc làm, khoảng 6,9 triệu người dùng đăng ký (2023).', industries: ['HRTECH'], geo: 'VN', roundDate: '2023-09' },
        { name: 'ITviec', url: 'https://itviec.com', whatTheyDo: 'Trang tuyển dụng dành cho lập trình viên và nhân sự công nghệ.', industries: ['HRTECH'], geo: 'VN' },
        { name: '1Office', whatTheyDo: 'Phần mềm quản trị doanh nghiệp; Mynavi đầu tư chiến lược ở vòng Series A.', industries: ['SAAS_B2B', 'HRTECH'], geo: 'VN', stage: 'SERIES_A', roundDate: '2023-05' },
        { name: 'MindX', whatTheyDo: 'Trường đào tạo công nghệ.', industries: ['EDTECH'], geo: 'VN' },
      ],
    },

    provenance: {
      sources: [
        { url: 'https://www.mynavi.jp/global-strategy/', fetchedAt: '2026-10-19', note: 'Trọng tâm đầu tư, danh mục Việt Nam, biểu mẫu liên hệ' },
        { url: 'https://mynavi.jp/global-strategy/?p=715', fetchedAt: '2026-10-19', note: 'Lập Mynavi Vietnam tại TP.HCM ngày 07/05/2024' },
        { url: 'https://mynavi.jp/eng/news/2023/09/post_39942.html', fetchedAt: '2026-10-19', note: 'Đầu tư thêm vào TopCV, thành công ty liên kết (25/09/2023)' },
        { url: 'https://media.dealstreetasia.com/stories/1office-japan-mynavi-344270', fetchedAt: '2026-10-19', note: 'Đầu tư chiến lược Series A vào 1Office (22/05/2023)' },
        { url: 'https://web-assets.bcg.com/69/30/a6baab2b47b8a58e7181953ae024/vietnam-innovation-private-capital-report-2025.pdf', fetchedAt: '2026-10-19', note: 'Danh sách nhà đầu tư hoạt động mạnh tại Việt Nam năm 2024' },
      ],
      lastVerifiedAt: '2026-10-19',
    },
  },

  {
    fundId: 'tnb-aura',
    name: 'TNB Aura',
    shortName: 'TNB Aura',
    slug: 'tnb-aura',
    type: 'VC',
    vietnamPresence: 'INVESTED',
    vietnamEvidence: 'Dẫn dắt Series A của Vuihoc (6 triệu USD) và GIMO (17,1 triệu USD, 07/2023); tham gia vòng của Révi (02/2024).',
    website: 'https://www.tnbaura.com',
    // Chưa tìm thấy kênh nhận hồ sơ chính thức (trang Liên hệ chặn truy cập tự động).
    // Theo quy tắc 4 ở đầu file: để DRAFT tới khi xác nhận được.
    publish: { status: 'DRAFT' },

    thesis: {
      capitalTypes: ['EQUITY'],
      industries: [],
      // Trang chủ: "Series A or Series B".
      stages: ['SERIES_A', 'LATER'],
      geos: ['SEA'],
      ticket: {
        min: usd(1_000_000), max: usd(4_000_000),
        basis: 'INITIAL_CHECK', confidence: 'MEDIUM', sourceType: 'OFFICIAL_STALE',
        hardFilter: false, verifiedAt: '2026-10-19',
      },
      businessModels: [],
      customerTypes: [],
      instruments: [],
    },

    process: {
      submitUrl: null,
      notes: 'Chưa xác nhận kênh nộp hồ sơ. Trang chính thức không có biểu mẫu nhận hồ sơ đọc được.',
    },

    content: {
      summary:
        'Quỹ đầu tư mạo hiểm tại Singapore, đầu tư Series A và B ở Đông Nam Á và Úc, mức 1 đến 4 triệu USD mỗi công ty '
        + 'theo trang chính thức. Năm 2023 cho biết dành 20% đến 40% vốn quỹ cho Việt Nam và coi Việt Nam là thị trường '
        + 'chính của quỹ thứ ba; có chương trình scout tìm startup giai đoạn sớm hơn.',
      lookingFor: [
        'Startup Đông Nam Á ở vòng Series A, nơi quỹ cho rằng mình hỗ trợ được nhiều nhất',
      ],
      tips: [
        'Đây là quỹ cho vòng Series A trở lên. Nếu đang gọi pre-seed hoặc seed, hỏi về chương trình scout thay vì nộp thẳng vào quỹ.',
        'Hai thương vụ Việt Nam lớn nhất (Vuihoc, GIMO) đều do quỹ dẫn dắt. Quỹ có thể đặt điều khoản cho cả vòng, nên chuẩn bị term sheet tham chiếu trước khi đàm phán.',
      ],
    },

    portfolio: {
      source: 'https://theinvestor.vn/singapores-tnb-aura-says-expected-third-fund-will-focus-on-vietnam-d6111.html',
      verifiedAt: '2026-10-19',
      note: 'Trang danh mục của quỹ chặn truy cập tự động; các dòng dưới đây lấy từ báo chí có ngày đăng.',
      items: [
        {
          name: 'Vuihoc', whatTheyDo: 'Nền tảng học trực tuyến cho học sinh phổ thông.', industries: ['EDTECH'], geo: 'VN', stage: 'SERIES_A', leadInvestor: true,
          roundSize: usd(6_000_000), roundSource: 'https://theinvestor.vn/singapores-tnb-aura-says-expected-third-fund-will-focus-on-vietnam-d6111.html',
        },
        {
          name: 'GIMO', whatTheyDo: 'Ứng lương linh hoạt cho công nhân nhà máy chưa tiếp cận dịch vụ ngân hàng.', industries: ['FINTECH'], geo: 'VN', stage: 'SERIES_A', roundDate: '2023-07', leadInvestor: true,
          roundSize: usd(17_100_000), roundSource: 'https://www.avcj.com/avcj/news/3029706/tnb-aura-leads-usd17m-series-a-for-vietnams-gimo',
        },
        { name: 'Révi', url: 'https://revi.vn', whatTheyDo: 'Chuỗi cà phê và trà ứng dụng công nghệ, do cựu lãnh đạo Gojek lập.', industries: ['FOODBEV'], geo: 'VN', roundDate: '2024-02' },
      ],
    },

    provenance: {
      sources: [
        { url: 'https://www.tnbaura.com', fetchedAt: '2026-10-19', note: 'Series A, B; 1 đến 4 triệu USD; Đông Nam Á và Úc (trang cũ, tin mới nhất 2020)' },
        { url: 'https://theinvestor.vn/singapores-tnb-aura-says-expected-third-fund-will-focus-on-vietnam-d6111.html', fetchedAt: '2026-10-19', note: 'Quỹ 3 lấy Việt Nam làm thị trường chính, 20% đến 40% vốn cho Việt Nam (07/08/2023)' },
        { url: 'https://www.avcj.com/avcj/news/3029706/tnb-aura-leads-usd17m-series-a-for-vietnams-gimo', fetchedAt: '2026-10-19', note: 'Dẫn dắt Series A 17,1 triệu USD của GIMO (26/07/2023)' },
        { url: 'https://www.dealstreetasia.com/?p=381404', fetchedAt: '2026-10-19', note: 'Tham gia vòng của Révi (02/02/2024)' },
        { url: 'https://web-assets.bcg.com/69/30/a6baab2b47b8a58e7181953ae024/vietnam-innovation-private-capital-report-2025.pdf', fetchedAt: '2026-10-19', note: 'Danh sách nhà đầu tư hoạt động mạnh tại Việt Nam năm 2024' },
      ],
      lastVerifiedAt: '2026-10-19',
    },
  },

  // ── Đợt 2 (21/10/2026): có thương vụ Việt Nam gần đây. DRAFT để My duyệt. ──
  {
    fundId: 'east-ventures',
    name: 'East Ventures',
    shortName: 'East Ventures',
    slug: 'east-ventures',
    type: 'VC',
    vietnamPresence: 'INVESTED',
    vietnamEvidence:
      'Rót vốn vào vòng gần 10 triệu USD của Galaxy Education (HOCMAI, ICAN, FUNiX), '
      + 'thông cáo chính thức ngày 25/06/2025.',
    website: 'https://east.vc',
    publish: { status: 'PUBLISHED' },

    thesis: {
      capitalTypes: ['EQUITY'],
      // Trang chính thức: "backing visionary founders from Seed to Growth stage".
      stages: ['SEED', 'SEED_PLUS', 'SERIES_A', 'LATER'],
      // "leading venture capital firm in Southeast Asia"; trang không nêu riêng Việt Nam.
      geos: ['SEA'],
      // Tự mô tả là "sector-agnostic" → để trống.
      industries: [],
      // Không công bố quy mô séc.
      businessModels: [],
      customerTypes: [],
      instruments: [],
    },

    process: {
      submitUrl: 'https://east.vc/contact-us/',
      ctaType: 'apply',
      notes: 'Biểu mẫu chính thức: chọn vai trò Startup, trả lời bốn câu hỏi (vấn đề, giải pháp, giá trị, thị trường) và tải pitch deck tối đa 5 MB.',
    },

    content: {
      summary:
        'Quỹ đầu tư mạo hiểm Đông Nam Á, văn phòng tại Singapore, Jakarta và Tokyo, '
        + 'đầu tư từ vòng seed tới giai đoạn tăng trưởng và không giới hạn ngành. '
        + 'Tại Việt Nam, quỹ rót vốn vào Galaxy Education năm 2025.',
      lookingFor: [
        'Startup công nghệ Đông Nam Á từ vòng seed trở lên',
        'Quỹ không giới hạn ngành; các chủ đề được nhắc nhiều là khí hậu, AI và chuyển đổi số',
      ],
      tips: [
        'Quỹ không công bố quy mô séc. Hỏi thẳng trong buổi đầu.',
        'Phần lớn danh mục và hoạt động của quỹ ở Indonesia. Trong hồ sơ, nói rõ vì sao thị trường Việt Nam của bạn đáng để một quỹ khu vực quan tâm.',
        'Biểu mẫu nhận pitch deck tối đa 5 MB, nên xuất file PDF nhẹ trước khi nộp.',
      ],
    },

    portfolio: {
      source: 'https://east.vc/news/press-release/vietnamese-edtech-galaxy-education-raises-nearly-us-10-million-from-east-ventures',
      verifiedAt: '2026-10-21',
      note: 'Chỉ ghi thương vụ Việt Nam có thông cáo chính thức của quỹ. Thông cáo không nêu tên vòng.',
      items: [
        {
          name: 'Galaxy Education',
          whatTheyDo: 'Tập đoàn giáo dục trực tuyến, vận hành HOCMAI, ICAN và FUNiX.',
          industries: ['EDTECH'], geo: 'VN', roundDate: '2025-06',
          roundSize: usd(10_000_000),
          roundSource: 'https://east.vc/news/press-release/vietnamese-edtech-galaxy-education-raises-nearly-us-10-million-from-east-ventures',
        },
      ],
    },

    provenance: {
      sources: [
        { url: 'https://east.vc', fetchedAt: '2026-10-21', note: 'Seed tới Growth; Đông Nam Á; sector-agnostic; văn phòng Singapore, Jakarta, Tokyo' },
        { url: 'https://east.vc/contact-us/', fetchedAt: '2026-10-21', note: 'Biểu mẫu nhận pitch cho startup' },
        { url: 'https://east.vc/news/press-release/vietnamese-edtech-galaxy-education-raises-nearly-us-10-million-from-east-ventures', fetchedAt: '2026-10-21', note: 'Galaxy Education gần 10 triệu USD (25/06/2025)' },
      ],
      lastVerifiedAt: '2026-10-21',
    },
  },

  {
    fundId: 'argor-capital',
    name: 'Argor Capital',
    shortName: 'Argor',
    slug: 'argor-capital',
    type: 'VC',
    vietnamPresence: 'INVESTED',
    vietnamEvidence:
      'Dẫn dắt vòng Series A 10 triệu USD của AI Hay (07/2025). Năm 2023 quỹ cho biết '
      + 'Indonesia và Việt Nam là hai thị trường trọng tâm.',
    website: 'https://www.argor.vc',
    publish: { status: 'DRAFT' },

    thesis: {
      capitalTypes: ['EQUITY'],
      // TechCrunch 19/06/2023: "early-stage and midstage companies".
      stages: ['SERIES_A', 'LATER'],
      // "with an emphasis on Indonesia and Vietnam as the key countries of focus".
      geos: ['SEA', 'VN'],
      industries: [],
      // TechCrunch 19/06/2023: khoản đầu tư ban đầu thường "$2 million -$10 million".
      ticket: {
        min: usd(2_000_000), max: usd(10_000_000),
        basis: 'INITIAL_CHECK', confidence: 'MEDIUM', sourceType: 'PRESS',
        hardFilter: false, verifiedAt: '2026-10-21',
      },
      businessModels: [],
      customerTypes: [],
      instruments: [],
    },

    process: {
      submitUrl: null,
      notes: 'Chưa xác nhận kênh nộp hồ sơ. Trang chính thức chỉ có mục liên hệ cho nhà đầu tư góp vốn (LP), không có biểu mẫu cho founder.',
    },

    content: {
      summary:
        'Quỹ đầu tư mạo hiểm tại Singapore, trước đây tên Go-Ventures, có GoTo (Indonesia) góp vốn '
        + 'nhưng quỹ cho biết tự quyết định đầu tư. Đầu tư startup Đông Nam Á giai đoạn đầu và giữa, '
        + 'lấy Indonesia và Việt Nam làm trọng tâm.',
      lookingFor: [
        'Startup đã qua vòng seed, cần khoản đầu tư ban đầu khoảng 2 đến 10 triệu USD (theo báo chí 2023)',
        'Các mảng quỹ từng nêu: sàn B2B, tiêu dùng, công nghệ môi trường, fintech, chuyển đổi số cho doanh nghiệp nhỏ',
      ],
      tips: [
        'Séc ban đầu từ khoảng 2 triệu USD nên quỹ không hợp với vòng angel hay pre-seed.',
        'Quỹ chưa có kênh nhận hồ sơ công khai. Cách khả thi là xin giới thiệu qua founder trong danh mục hoặc nhà đầu tư cùng vòng.',
      ],
    },

    portfolio: {
      source: 'https://technode.global/2025/07/02/vietnams-ai-hay-secures-10m-in-series-a-funding-round-led-by-argor-capital/',
      verifiedAt: '2026-10-21',
      note: 'Trang chính thức không công bố danh mục; dòng dưới đây lấy từ báo chí có ngày đăng.',
      items: [
        {
          name: 'AI Hay',
          whatTheyDo: 'Ứng dụng hỏi đáp và tìm kiếm bằng AI tạo sinh cho người Việt.',
          industries: ['AI'], geo: 'VN', stage: 'SERIES_A', roundDate: '2025-07', leadInvestor: true,
          roundSize: usd(10_000_000),
          roundSource: 'https://technode.global/2025/07/02/vietnams-ai-hay-secures-10m-in-series-a-funding-round-led-by-argor-capital/',
        },
      ],
    },

    provenance: {
      sources: [
        { url: 'https://www.argor.vc', fetchedAt: '2026-10-21', note: 'Đông Nam Á; trụ sở Singapore, được MAS cấp phép; chỉ có liên hệ cho LP' },
        { url: 'https://techcrunch.com/2023/06/19/southeast-asia-focused-go-ventures-closes-240m-second-fund-rebrands-as-argor-capital', fetchedAt: '2026-10-21', note: 'Quỹ 2: 240 triệu USD; séc 2 đến 10 triệu USD; trọng tâm Indonesia và Việt Nam (19/06/2023)' },
        { url: 'https://technode.global/2025/07/02/vietnams-ai-hay-secures-10m-in-series-a-funding-round-led-by-argor-capital/', fetchedAt: '2026-10-21', note: 'Dẫn dắt Series A 10 triệu USD của AI Hay (02/07/2025)' },
      ],
      lastVerifiedAt: '2026-10-21',
    },
  },
];

/**
 * Danh sách dùng trên website: dữ liệu gốc ở trên + bản trên Firestore (sửa ở
 * /admin/nguon-von), kéo về lúc build bằng scripts/fetch-funds.mjs.
 * Trình duyệt còn đọc Firestore lần nữa khi chạy (src/lib/useFunds.js).
 */
export const FUNDS = gopQuy(FUNDS_GOC, REMOTE_FUNDS);

/** Số quỹ thực sự hiển thị cho người dùng. */
export const SO_QUY_HIEN_THI = FUNDS.filter(
  (f) => f.publish?.status === 'PUBLISHED' && !f.isExample
).length;

/**
 * Danh sách còn mỏng thì giao diện phải nói rõ. Người dùng thấy hai quỹ và
 * tưởng đó là toàn bộ thị trường thì còn tệ hơn là không có công cụ.
 */
export const DANH_SACH_CON_MONG = SO_QUY_HIEN_THI < 8;

export const CO_DU_LIEU_THAT = SO_QUY_HIEN_THI > 0;
